/**
 * Luigi's Box PLP + search results (Empire theme).
 *
 * Two entry modes:
 * - Search: `#lb-layout[data-lb-search]` — uses `#lb-search-results-form` / input (names must NOT
 *   collide with `lb-autocomplete.js`, which declares `searchForm` / `searchInput`).
 * - Collection: `#lb-layout[data-lb-collection]` — Luigi filter value in `data-lb-plp-category` (must
 *   match feed: title vs handle per section setting); `data-lb-plp-category-identity` = catalog path for analytics (e.g. `/collections/beige-rugs`).
 */

let searchResultsForm;
let searchResultsInput;
let categoryTitle;
/** Catalog category path — `scopes.CategoryIdentity` (vs Luigi feed label in `data-lb-plp-category`). */
let categoryIdentity;

if (document.querySelector("[data-lb-search]")) {
    searchResultsForm = document.getElementById("lb-search-results-form");
    searchResultsInput = document.getElementById("lb-search-results-input");
} else {
    const el = document.querySelector("[data-lb-collection]");
    categoryTitle = el ? el.dataset.lbPlpCategory || null : null;
    categoryIdentity = el ? el.dataset.lbPlpCategoryIdentity || null : null;
}

/** Luigi `plp` + `f[]=category:…`; null on search templates (category filter only on collection PLP). */
function lbPlCategoryFilter() {
    return categoryTitle
        ? { field: "category", value: categoryTitle, identity: categoryIdentity || null }
        : null;
}

const resultsContainer = document.getElementById("lb-results-container");
const facetsContainer = document.getElementById("lb-facets-container");
const resultsHeading = document.getElementById("lb-results-heading");
const paginationContainer = document.getElementById("lb-pagination-container");
const sortSelect = document.getElementById("lb-sort-select");
const drawerOverlay = document.getElementById("lb-filter-drawer-overlay");
const drawerBody = document.getElementById("lb-filter-drawer-body");
const mobileBtn = document.getElementById("lb-filter-mobile-btn");
const drawerClose = document.getElementById("lb-filter-drawer-close");
const layoutParent = document.querySelector("[data-pagination-amount]");

/** 1×1 transparent GIF — first paint when we skip Luigi `image_link` so hydration can set Shopify `featured_media` without swapping from a misleading variant shot. */
const LB_GRID_IMAGE_PLACEHOLDER =
    "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";

/** After pagination, bring the product grid to the top of the viewport (see `#lb-results-container` scroll-margin in `lb-custom.css`). */
function scrollLbResultsIntoView() {
    if (!resultsContainer) return;
    requestAnimationFrame(() => {
        resultsContainer.scrollIntoView({ behavior: "auto", block: "start" });
    });
}

/** Cancels the previous Luigi request so a slow `featured` response can’t overwrite a newer `title:desc` response. */
let lbFetchAbort = null;

/** Monotonic id: only the latest `getLBResults` may apply hits to the DOM (guards stale responses if abort is late). */
let lbLuigiFetchEpoch = 0;

/** Re-entrant guard: syncing `#lb-sort-select` from URL must not fire `change` → second `getLBResults` (same frame). */
let lbIgnoreSortChangeDepth = 0;

function lbWithIgnoredSortSelectChange(fn) {
    lbIgnoreSortChangeDepth++;
    try {
        fn();
    } finally {
        lbIgnoreSortChangeDepth--;
    }
}

/** Bumps when a new results payload is applied so stale grid hydration (SA / Shopify `.js`) does not run after a newer fetch. */
let lbResultsHydrationGen = 0;

// `<option value>` = Shopify `sort_by` key; `data-lb-sort` = Luigi `sort` param (`field:direction` or empty).
function getSelectedLuigiSort() {
    if (!sortSelect) return "";
    const opt = sortSelect.selectedOptions[0];
    if (!opt) return "";
    const fromAttr = opt.hasAttribute("data-lb-sort") ? opt.getAttribute("data-lb-sort") : null;
    if (fromAttr) return fromAttr;
    // If Liquid left `data-lb-sort` blank (unknown option), map Shopify keys so we still send a valid `sort`.
    const v = opt.value || "";
    switch (v) {
        case "title-ascending":
            return "title:asc";
        case "title-descending":
            return "title:desc";
        case "price-ascending":
            return "price_amount:asc";
        case "price-descending":
            return "price_amount:desc";
        case "created-ascending":
            return "created_at:asc";
        case "created-descending":
            return "created_at:desc";
        default:
            return "";
    }
}

// Mobile filter drawer: overlay + `#lb-filter-drawer-body` mirror of desktop facet markup (filled in `renderFacets`).
function openDrawer() {
    if (drawerOverlay) {
        drawerOverlay.classList.add("is-open");
        drawerOverlay.setAttribute("aria-hidden", "false");
    }
    document.body.style.overflow = "hidden";
}

function closeDrawer() {
    if (drawerOverlay) {
        drawerOverlay.classList.remove("is-open");
        drawerOverlay.setAttribute("aria-hidden", "true");
    }
    document.body.style.overflow = "";
}

if (mobileBtn) {
    mobileBtn.addEventListener("click", openDrawer);
}

if (drawerClose) {
    drawerClose.addEventListener("click", closeDrawer);
}

if (drawerOverlay) {
    drawerOverlay.addEventListener("click", e => { 
        if (e.target === drawerOverlay) closeDrawer(); 
    });
}

let activeFilters = {};
let currentPage = 1;

const RESULTS_TRACKER_ID = "749111-1060005";
const API_ENDPOINT = "https://live.luigisbox.com/search";
const RESULTS_PER_PAGE = layoutParent ? layoutParent.dataset.paginationAmount : 24;

/** Parses theme setting `lb_search_facets` (see `javascript-lang.liquid` → `window.theme.lbSearchFacetsList`). */
function getLbFacetsQueryParam() {
    const raw = window.theme && window.theme.lbSearchFacetsList;
    const s = typeof raw === "string" ? raw.trim() : "";
    if (!s) return "";
    const parts = s
        // Split only between facet entries, never on spaces inside facet names.
        .split(/[,\n;]+/)
        .map((x) => x.trim())
        .filter(Boolean);
    return [...new Set(parts)].join(",");
}

function defaultLbFacetLabel(name) {
    return String(name || "")
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());
}

function parseLbFacetLabelOverrides(raw) {
    const map = new Map();
    const s = typeof raw === "string" ? raw : "";
    if (!s.trim()) return map;
    const lines = s.split(/\r?\n/);
    for (const line of lines) {
        const txt = String(line || "").trim();
        if (!txt || txt.startsWith("#")) continue;
        const match = txt.match(/^(.+?)(?:=|:|=>)(.+)$/);
        if (!match) continue;
        const source = String(match[1] || "").trim();
        const target = String(match[2] || "").trim();
        if (!source || !target) continue;
        map.set(source.toLowerCase(), target);
    }
    return map;
}

let _lbFacetLabelOvRaw;
let _lbFacetLabelOvMap;
function lbFacetLabelOverridesMap() {
    const raw = window.theme && window.theme.lbFacetLabelOverrides;
    if (_lbFacetLabelOvRaw === raw && _lbFacetLabelOvMap) return _lbFacetLabelOvMap;
    _lbFacetLabelOvRaw = raw;
    _lbFacetLabelOvMap = parseLbFacetLabelOverrides(raw);
    return _lbFacetLabelOvMap;
}

function parseLbFacetValueOrderOverrides(raw) {
    const map = new Map();
    const s = typeof raw === "string" ? raw : "";
    if (!s.trim()) return map;
    const lines = s.split(/\r?\n/);
    for (const line of lines) {
        const txt = String(line || "").trim();
        if (!txt || txt.startsWith("#")) continue;
        const match = txt.match(/^(.+?)(?:=|:|=>)(.+)$/);
        if (!match) continue;
        const facetName = String(match[1] || "").trim().toLowerCase();
        const valuesRaw = String(match[2] || "").trim();
        if (!facetName || !valuesRaw) continue;
        const ordered = valuesRaw
            .split("|")
            .map((v) => String(v || "").trim())
            .filter(Boolean);
        if (!ordered.length) continue;
        map.set(facetName, ordered);
    }
    return map;
}

let _lbFacetValOrderRaw;
let _lbFacetValOrderMap;
function lbFacetValueOrderOverridesMap() {
    const raw = window.theme && window.theme.lbFacetValueOrderOverrides;
    if (_lbFacetValOrderRaw === raw && _lbFacetValOrderMap) return _lbFacetValOrderMap;
    _lbFacetValOrderRaw = raw;
    _lbFacetValOrderMap = parseLbFacetValueOrderOverrides(raw);
    return _lbFacetValOrderMap;
}

function orderLbFacetValues(facetName, values) {
    const list = Array.isArray(values) ? [...values] : [];
    if (!list.length) return list;
    const overrides = lbFacetValueOrderOverridesMap();
    const wanted = overrides.get(String(facetName || "").trim().toLowerCase());
    if (!wanted || !wanted.length) return list;

    const rank = new Map(wanted.map((v, i) => [v.toLowerCase(), i]));
    return list.sort((a, b) => {
        const aVal = String((a && a.value) || "").trim().toLowerCase();
        const bVal = String((b && b.value) || "").trim().toLowerCase();
        const aRank = rank.has(aVal) ? rank.get(aVal) : Number.POSITIVE_INFINITY;
        const bRank = rank.has(bVal) ? rank.get(bVal) : Number.POSITIVE_INFINITY;
        if (aRank !== bRank) return aRank - bRank;
        return 0;
    });
}

/** Luigi facet name in `lb_facet_value_order_overrides` / Search API (matches `orderLbFacetValues` keys). */
const LB_SIZE_FACET_ORDER_KEY = "size filter with custom";

/**
 * Sort PLP/search card size rows to match facet value order from theme settings (same pipe-list as the Size filter).
 */
function sortLbSizeRowsForCard(sizeRows) {
    if (!Array.isArray(sizeRows) || sizeRows.length < 2) return sizeRows;
    const overrides = lbFacetValueOrderOverridesMap();
    const wanted = overrides.get(LB_SIZE_FACET_ORDER_KEY);
    if (!wanted || !wanted.length) return sizeRows;

    const rank = new Map(wanted.map((v, i) => [String(v).trim().toLowerCase(), i]));
    return [...sizeRows].sort((a, b) => {
        const aVal = String((a && a.title) || "").trim().toLowerCase();
        const bVal = String((b && b.title) || "").trim().toLowerCase();
        const aRank = rank.has(aVal) ? rank.get(aVal) : Number.POSITIVE_INFINITY;
        const bRank = rank.has(bVal) ? rank.get(bVal) : Number.POSITIVE_INFINITY;
        if (aRank !== bRank) return aRank - bRank;
        const ap = a.price != null ? Number(a.price) : NaN;
        const bp = b.price != null ? Number(b.price) : NaN;
        if (!Number.isNaN(ap) && !Number.isNaN(bp) && ap !== bp) {
            const priceDesc = getSelectedLuigiSort() === "price_amount:desc";
            return priceDesc ? bp - ap : ap - bp;
        }
        return aVal.localeCompare(bVal);
    });
}

function getLbFacetDisplayLabel(name) {
    const rawName = String(name || "").trim();
    if (!rawName) return "";
    const overrideMap = lbFacetLabelOverridesMap();
    const override = overrideMap.get(rawName.toLowerCase());
    if (override) return override;

    const str = (window.theme && window.theme.strings) || {};
    if (rawName.toLowerCase() === "size filter with custom") {
        const locale = String(str.lbFacetLabelSizeFilterWithCustom || "").trim();
        if (locale) return locale;
    }
    if (rawName.toLowerCase() === LB_PRICE_AMOUNT_FACET) {
        const localePrice = String(str.lbPriceFacetLabel || "").trim();
        if (localePrice) return localePrice;
    }

    return defaultLbFacetLabel(rawName);
}

/** Last `size` sent to Luigi (PLP may be reduced when a promo tile is spliced into the grid). */
let lastLuigiRequestPageSize = parseInt(String(RESULTS_PER_PAGE), 10) || 24;

function lbProductGridColumnCount() {
    if (!resultsContainer) return 4;
    const m = resultsContainer.className.match(/products-per-row-(\d+)/);
    return m ? Math.max(1, parseInt(m[1], 10)) : 4;
}

/** PLP discount promo templates present + theme flag (same gates as `getLbCollectionDiscountPromoHtml`). */
function lbPlpDiscountPromoMayRender() {
    if (
        !document.querySelector("[data-lb-collection]") &&
        !document.querySelector("[data-lb-search]")
    ) {
        return false;
    }
    if (!window.theme || !window.theme.lbDiscountPromoEnabled) return false;
    return ["lb-plp-discount-slot-1", "lb-plp-discount-slot-2", "lb-plp-discount-slot-3"].some((id) => {
        const t = document.getElementById(id);
        return t && t.innerHTML && t.innerHTML.trim();
    });
}

/** Theme “collection spray” tile (`static-collection` / `lb-collection` / `lb-search-results` + `renderLbResultsGrid`). */
function lbPlpCollectionSprayConfigured() {
    const layout = document.getElementById("lb-layout");
    if (!layout) return false;
    const sprayAtAttr = (layout.getAttribute("data-lb-spray-at") || layout.dataset.lbSprayAt || "").trim();
    if (!sprayAtAttr) return false;
    const tmpl = document.getElementById("lb-plp-collection-spray-slot");
    return Boolean(tmpl && tmpl.innerHTML && tmpl.innerHTML.trim());
}

function lbCollectionSprayTriggerIndex() {
    const layout = document.getElementById("lb-layout");
    if (!layout) return 0;
    const raw = (layout.getAttribute("data-lb-spray-at") || layout.dataset.lbSprayAt || "").trim();
    const n = parseInt(String(raw), 10);
    return Number.isFinite(n) && n > 0 ? n : 0;
}

function getLbCollectionSprayHtml() {
    if (!lbPlpCollectionSprayConfigured()) return "";
    const tmpl = document.getElementById("lb-plp-collection-spray-slot");
    if (!tmpl || !tmpl.innerHTML) return "";
    return tmpl.innerHTML.trim();
}

/** `sprayTriggerForloop1Based` matches Liquid `forloop.index` (theme setting `collection_spray_pos` minus 1). */
function lbCollectionSpraySpliceIndex(sprayTriggerForloop1Based, discountInserted) {
    const t = sprayTriggerForloop1Based;
    const productIdx0 = t - 1;
    if (!discountInserted) return productIdx0;
    return productIdx0 >= 2 ? productIdx0 + 1 : productIdx0;
}

/** Both promos when possible; else spray; else discount. Row padding: `lbPlpAppendGridRowFillers`. */
function lbResolvePlpPromoInserts(hitsLen, canDiscount, sprayBaseOk, sprayAt) {
    let insertDiscount = false;
    let insertSpray = false;
    const canSpray = Boolean(sprayBaseOk && sprayAt > 0 && hitsLen >= sprayAt);
    if (canDiscount && canSpray) {
        insertDiscount = true;
        insertSpray = true;
    } else if (canSpray) {
        insertSpray = true;
    } else if (canDiscount) {
        insertDiscount = true;
    }
    return { insertDiscount, insertSpray };
}

/** Invisible flex items so `nth-child` gutter rules stay aligned on the last row. */
function lbPlpAppendGridRowFillers(parts, cols) {
    const rem = parts.length % cols;
    if (rem === 0) return;
    const need = cols - rem;
    const filler =
        '<div class="productgrid--item lb-plp-grid-filler imagestyle--no-image" aria-hidden="true"></div>';
    for (let i = 0; i < need; i++) parts.push(filler);
}

/** How many non-product tiles we may add on a full PLP (discount at 3 + optional collection spray). */
function lbPlpExtraGridSlotCount() {
    let n = 0;
    if (lbPlpDiscountPromoMayRender()) n += 1;
    if (lbPlpCollectionSprayConfigured()) n += 1;
    return n;
}

/**
 * Collection PLP and Luigi search may splice discount + collection spray. Pick `size` so
 * `(size + extraSlots) % columns === 0` on full pages to avoid an empty last row.
 */
function computeLuigiRequestPageSize(applyPromoSlotSizing) {
    const base = parseInt(String(RESULTS_PER_PAGE), 10) || 24;
    if (!applyPromoSlotSizing) return base;
    const extra = lbPlpExtraGridSlotCount();
    if (extra === 0) return base;
    const cols = lbProductGridColumnCount();
    const k = (base + extra) % cols;
    if (k === 0) return base;
    return Math.max(1, base - k);
}

/** Facet key for price; Luigi numeric range filters use `min | max` (spaces); static buckets use `lo|hi` without spaces (same as Shopify-derived ranges). */
const LB_PRICE_AMOUNT_FACET = "price_amount";

/** Luigi facet names that use swatches (labels + `window.theme.lbColourHexByLabel` from `lb-colour-hex-map-json`). */
const LB_COLOUR_FACET_NAMES = new Set(["Colour", "Color"]);

/** Sidebar cap for long facet lists; brand/vendor show in full (see `getLbFacetsQueryParam` for API `name:count`). */
const LB_FACET_VALUES_PREVIEW_CAP = 8;

function lbFacetUsesFullValueList(facetName) {
    const n = String(facetName || "").trim().toLowerCase();
    return n === "brand" || n === "vendor" || n === "vendors";
}

function lbSanitizeHexForCss(hex) {
    const s = String(hex == null ? "" : hex).trim();
    return /^#[0-9A-Fa-f]{3,8}$/.test(s) ? s : "";
}

function lbHexForColourLabel(label) {
    const map = window.theme && window.theme.lbColourHexByLabel;
    if (!map || typeof map !== "object") return "";
    const pickHex = (key) => {
        if (key == null || !Object.prototype.hasOwnProperty.call(map, key)) return "";
        const v = map[key];
        const s = v == null ? "" : String(v).trim();
        return lbSanitizeHexForCss(s);
    };
    const raw = String(label ?? "");
    const direct = pickHex(raw) || pickHex(raw.trim());
    if (direct) return direct;
    const lower = raw.trim().toLowerCase();
    for (const k of Object.keys(map)) {
        if (String(k).trim().toLowerCase() === lower) return pickHex(k);
    }
    return "";
}

function renderLbColourFacetOptions(facetName, values, activeValuesRaw) {
    return values
        .map((v) => {
            const val = v.value;
            const hits = v.hits_count ?? 0;
            const trimmed = String(val || "").trim();
            const isMulti = /^multicoloured$/i.test(trimmed);
            const hexSafe = lbHexForColourLabel(val);
            const cssName = trimmed.toLowerCase().replace(/[^a-z]/g, "");
            const swatchStyle =
                !isMulti && hexSafe
                    ? ` style="background-color:${escapeHtmlAttr(hexSafe)};"`
                    : !isMulti && cssName
                      ? ` style="background-color:${escapeHtmlAttr(cssName)};"`
                      : "";
            const checked = lbActiveFacetListIncludesMergeKey(activeValuesRaw, val);
            const swatch = isMulti
                ? `<span class="collection-filters__filter-colour-item multicoloured"></span>`
                : `<span class="collection-filters__filter-colour-item"${swatchStyle}></span>`;
            return `
        <li class="collection-filters__filter-list-item">
          <label class="collection-filters__filter-link"${checked ? " data-filter-active" : ""} tabindex="0">
            <input type="checkbox" class="visually-hidden" tabindex="-1"
              data-facet="${escapeHtmlAttr(facetName)}"
              data-value="${escapeHtmlAttr(val)}"
              ${checked ? "checked" : ""}>
            ${swatch}
            <span class="collection-filters__filter-list-item-text collection-filters__filter-list-item-colour">
              ${escapeHtmlText(val)}
              <span class="text-doveGray">(${hits})</span>
            </span>
          </label>
        </li>`;
        })
        .join("");
}

/** Trim ends and collapse internal whitespace (display + dedupe key basis). */
function lbFacetValueDisplayTrim(value) {
    return String(value ?? "")
        .trim()
        .replace(/\s+/g, " ");
}

/** Case- and spacing-insensitive key so API variants (“Wool”, “wool ”) merge in the UI. */
function lbFacetOptionRowMergeKey(value) {
    return lbFacetValueDisplayTrim(value).toLowerCase();
}

function lbActiveFacetListIncludesMergeKey(activeList, candidateValue) {
    if (!Array.isArray(activeList) || !activeList.length) return false;
    const k = lbFacetOptionRowMergeKey(candidateValue);
    return activeList.some((v) => lbFacetOptionRowMergeKey(v) === k);
}

function lbRemoveActiveFacetValuesByMergeKey(arr, valueToRemove) {
    if (!Array.isArray(arr)) return [];
    const k = lbFacetOptionRowMergeKey(valueToRemove);
    return arr.filter((v) => lbFacetOptionRowMergeKey(v) !== k);
}

/**
 * Merge duplicate facet option rows from Luigi (spacing / casing / duplicate feed values).
 * Canonical `value` is the spelling from the row with the highest `hits_count` (ties → first in API order); counts are summed.
 */
function normalizeLbFacetOptionRows(values) {
    const list = Array.isArray(values) ? values : [];
    const byKey = new Map();
    for (let i = 0; i < list.length; i++) {
        const row = list[i];
        const display = lbFacetValueDisplayTrim(row && row.value);
        if (!display) continue;
        const key = lbFacetOptionRowMergeKey(display);
        const hits = Number((row && row.hits_count) || 0);
        const safeHits = Number.isFinite(hits) ? hits : 0;
        const cur = byKey.get(key);
        if (!cur) {
            byKey.set(key, {
                value: display,
                totalHits: safeHits,
                bestHits: safeHits,
            });
        } else {
            cur.totalHits += safeHits;
            if (safeHits > cur.bestHits) {
                cur.bestHits = safeHits;
                cur.value = display;
            }
        }
    }
    return [...byKey.values()].map(({ value, totalHits }) => ({
        value,
        hits_count: totalHits,
    }));
}

/** Drop duplicate “field:value” chips when values only differ by case / spacing. */
function dedupeLbAppliedFilterStrings(filters) {
    const seen = new Set();
    const out = [];
    for (const f of filters) {
        const colonIndex = f.indexOf(":");
        if (colonIndex === -1) {
            out.push(f);
            continue;
        }
        const field = f.slice(0, colonIndex);
        const value = f.slice(colonIndex + 1);
        const key =
            field === LB_PRICE_AMOUNT_FACET
                ? `${field}:${value}`
                : `${field}:${lbFacetOptionRowMergeKey(value)}`;
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(f);
    }
    return out;
}

/** Upper bound for “£500 or more” — Luigi needs a numeric max on range filters. */
const LB_PRICE_BUCKET_MAX_OPEN = 999999999;

/**
 * Fixed buckets aligned with live collection `filter.v.price` (gte/lte).
 * `filterValue` is sent as `f[]=price_amount:lo|hi` (no spaces).
 */
const LB_PRICE_STATIC_BUCKETS = [
    { label: "Under £50", filterValue: `0|50` },
    { label: "£50 to £100", filterValue: `50|100` },
    { label: "£100 to £200", filterValue: `100|200` },
    { label: "£200 to £300", filterValue: `200|300` },
    { label: "£300 to £400", filterValue: `300|400` },
    { label: "£400 to £500", filterValue: `400|500` },
    { label: "£500 or more", filterValue: `500|${LB_PRICE_BUCKET_MAX_OPEN}` },
];

function lbStaticPriceBucketLabelForFilterValue(filterValue) {
    const row = LB_PRICE_STATIC_BUCKETS.find((b) => b.filterValue === filterValue);
    return row ? row.label : null;
}

// Shopper Approved stars: section can force on/off via `#lb-layout[data-show-shopper-approved]`; else theme setting.
function lbShowShopperApprovedStars() {
    const lb = document.getElementById("lb-layout");
    if (lb && lb.dataset && lb.dataset.showShopperApproved === "true") return true;
    if (lb && lb.dataset && lb.dataset.showShopperApproved === "false") return false;
    return !!(window.theme && window.theme.displayShopperApprovedRating);
}

/**
 * PLP primary image rules (Liquid `lb-collection.liquid` + this file must agree):
 * - Collection handle/title contains `outdoor` → outdoor lifestyle when available (`data-lb-plp-use-outdoor-image` + `lbShouldUseOutdoorImage`).
 * - Active facet on style/type/room/outdoor with an outdoor-ish value → same as above.
 * - Collection handle/title contains `runner` (covers "runners") or Size facet implies runner → runner variant image (`data-lb-plp-shape-variant-mode` + `lbActiveShapeVariantMode`).
 * - Collection handle/title contains `round` or `circle`, or Shape facet circle/round → round variant image.
 * - Generic PLP (no outdoor intent, no shape-led mode): first paint uses a transparent placeholder; `hydrateLbCardPrimaryImageFromShopifyProduct` sets Shopify featured so Luigi `image_link` (often a variant/runner crop) does not flash then swap.
 *
 * `lbLayoutUsesOutdoorPlpImage`: matches `product-grid-item` — outdoor collections use `custom.outdoor_image` when set; needs Luigi `hit_fields` + feed mapping (see getLBResults).
 */
function lbLayoutUsesOutdoorPlpImage() {
    const lb = document.getElementById("lb-layout");
    return !!(lb && lb.dataset && lb.dataset.lbPlpUseOutdoorImage === "true");
}

/** Outdoor grid: collection `data-lb-plp-use-outdoor-image`, or Style/Type/Room (etc.) filter values mention outdoor. */
function lbShouldUseOutdoorImage() {
    if (lbLayoutUsesOutdoorPlpImage()) return true;
    const filters = activeFilters || {};
    for (const [facetName, values] of Object.entries(filters)) {
        const fn = String(facetName || "").toLowerCase();
        if (
            !fn.includes("style") &&
            !fn.includes("type") &&
            !fn.includes("room") &&
            !fn.includes("outdoor")
        ) {
            continue;
        }
        if (!Array.isArray(values)) continue;
        for (const value of values) {
            const s = String(value || "").trim().toLowerCase();
            if (s && (s === "outdoor" || /\boutdoor\b/.test(s))) return true;
        }
    }
    return false;
}

/** `runner` | `round` from `#lb-layout[data-lb-plp-shape-variant-mode]` — matches `product-grid-item` shape-led PLP image. */
function lbLayoutPlpShapeVariantMode() {
    const lb = document.getElementById("lb-layout");
    const m = lb && lb.dataset ? lb.dataset.lbPlpShapeVariantMode : null;
    if (m === "runner" || m === "round") return m;
    return null;
}

/** Shape-led imagery: collection `data-lb-plp-shape-variant-mode`, or active `Shape` / `Size` (etc.) facets with runner vs round values — same value rules as before; facet name filter used to be `shape` only, which missed `Size=Runner`. */
function lbActiveShapeVariantMode() {
    const filters = activeFilters || {};
    const shapeFacetEntries = Object.entries(filters).filter(([facetName]) => {
        const fn = String(facetName || "").toLowerCase().trim();
        if (fn.includes("shape")) return true;
        if (/\bsizes?\b/.test(fn)) return true;
        if (fn.includes("option_size")) return true;
        return false;
    });

    if (shapeFacetEntries.length) {
        let wantsRunner = false;
        let wantsRound = false;
        for (const [, values] of shapeFacetEntries) {
            if (!Array.isArray(values)) continue;
            for (const value of values) {
                const s = String(value || "").trim().toLowerCase();
                if (!s) continue;
                if (s === "runner" || s.includes("runner") || s.includes("rectangle")) wantsRunner = true;
                if (s === "round" || s === "circle" || s.includes("round") || s.includes("circle")) wantsRound = true;
            }
        }
        if (wantsRunner && wantsRound) return null;
        if (wantsRunner) return "runner";
        if (wantsRound) return "round";
    }

    return lbLayoutPlpShapeVariantMode();
}

// Luigi often returns attribute values as arrays or nested structures; normalize to a single primitive.
function firstScalar(value) {
    if (value == null || value === "") return null;
    if (Array.isArray(value)) return firstScalar(value[0]);
    if (typeof value === "object") return null;
    return value;
}

/** Image metafields may be a URL string or `{ url: "..." }` from the feed. */
function lbScalarImageUrl(raw) {
    if (raw == null || raw === "") return null;
    if (Array.isArray(raw)) return lbScalarImageUrl(raw[0]);
    if (typeof raw === "object") {
        if (raw.url) return lbScalarImageUrl(raw.url);
        if (raw.src) return lbScalarImageUrl(raw.src);
        if (raw.image && typeof raw.image === "object") {
            const nested = raw.image.url || raw.image.src;
            if (nested) return lbScalarImageUrl(nested);
        }
    }
    const s = firstScalar(raw);
    return s != null ? String(s).trim() : null;
}

function lbOutdoorImageFromAttributes(attributes, nested = []) {
    const attr = attributes || {};
    const rows = [attr];
    if (Array.isArray(nested) && nested.length) {
        nested.forEach((row) => {
            if (row && typeof row === "object" && row.attributes) rows.push(row.attributes);
        });
    }

    for (const a of rows) {
        const direct =
            lbScalarImageUrl(a.outdoor_image) ||
            lbScalarImageUrl(a["custom.outdoor_image"]) ||
            lbScalarImageUrl(a.custom_outdoor_image) ||
            lbScalarImageUrl(a["metafields.custom.outdoor_image"]) ||
            lbScalarImageUrl(a["Outdoor image"]);
        if (direct) return direct;

        for (const [rawKey, rawVal] of Object.entries(a)) {
            const key = String(rawKey || "")
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, "");
            if (!key) continue;
            if (key.includes("outdoor") && key.includes("image")) {
                const u = lbScalarImageUrl(rawVal);
                if (u) return u;
            }
        }
    }
    return null;
}

function lbCardPrimaryImageSrc(attributes, nested = []) {
    const a = attributes || {};
    const mode = lbActiveShapeVariantMode();
    if (lbShouldUseOutdoorImage()) {
        const outdoor = lbOutdoorImageFromAttributes(a, nested);
        if (outdoor) return outdoor;
        /* Outdoor intent but no feed hero: keep `image_link` — hydrator keeps it when JSON has no outdoor metafield. */
        const linkOutdoor = lbScalarImageUrl(a.image_link);
        return linkOutdoor || "";
    }
    /*
     * Generic PLP (no shape-led mode): Luigi `image_link` is often a variant/listing crop (users read it as
     * “runner”) while Shopify PLP uses `featured_media`. Card markup uses `LB_GRID_IMAGE_PLACEHOLDER` when this
     * returns "" so hydration can set featured without a visible swap.
     */
    if (!mode) {
        return "";
    }
    const link = lbScalarImageUrl(a.image_link);
    return link || "";
}

function hitSortTitle(hit) {
    const a = hit.attributes || {};
    return String(firstScalar(a.title_common ?? a.title) ?? "");
}

/**
 * Single numeric for re-sorting hits to align with card pricing: uses nested variant `price_amount`
 * (current/sale) via `lbAggregatePricing` when variants exist — parent-only `price_amount` is often
 * list/compare and breaks sale sorting. Asc: **min** variant price; desc: **max** (Shopify PLP parity).
 */
function lbHitPriceSortKey(hit, desc) {
    const agg = lbAggregatePricing(hit.attributes || {}, hit.nested || []);
    const raw = desc ? agg.priceMax : agg.priceMin;
    if (raw != null && Number.isFinite(raw)) return raw;
    const fallback = parseFloat(firstScalar((hit.attributes || {}).price_amount));
    return Number.isFinite(fallback) ? fallback : null;
}

/**
 * Luigi merchandising can pin hits to the top while other rows respect `sort` (see API `pinned` on hits).
 * Re-sort the current page so A–Z / Z–A / price match Shopify when an explicit `sort` was requested.
 */
function stabilizeSortedHits(hits, lbSort) {
    if (!hits || hits.length < 2 || !lbSort) return hits;
    if (lbSort === "title:asc" || lbSort === "title:desc") {
        const desc = lbSort === "title:desc";
        return [...hits].sort((a, b) => {
            const c = hitSortTitle(a).localeCompare(hitSortTitle(b), undefined, {
                numeric: true,
                sensitivity: "base",
            });
            return desc ? -c : c;
        });
    }
    if (lbSort === "price_amount:asc" || lbSort === "price_amount:desc") {
        const desc = lbSort === "price_amount:desc";
        return [...hits].sort((a, b) => {
            const pa = lbHitPriceSortKey(a, desc);
            const pb = lbHitPriceSortKey(b, desc);
            const na = Number.isFinite(pa) ? pa : desc ? -Infinity : Infinity;
            const nb = Number.isFinite(pb) ? pb : desc ? -Infinity : Infinity;
            if (na === nb) return 0;
            const cmp = na < nb ? -1 : 1;
            return desc ? -cmp : cmp;
        });
    }
    return hits;
}

/** `Shopify_GB_{numericProductId}` for Shopper Approved aggregates; parses `shopify_CC_pid_vid` anywhere in `s`. */
function lbShopperApprovedGbFromCatalogString(s) {
    if (s == null || s === "") return null;
    const m = String(s).match(/shopify_[A-Za-z]{2}_(\d+)_(\d+)/i);
    return m ? `Shopify_GB_${m[1]}` : null;
}

// Map a hit to Shopper Approved's `Shopify_GB_{productId}` class when the numeric Shopify product id is present
// in attributes, nested variants, or a GID-style URL fragment.
function shopperApprovedClassFromHit(hit) {
    if (!hit) return null;

    const toClass = (v) => {
        const s = firstScalar(v);
        if (s == null || s === "") return null;
        let x = String(s).trim();
        if (!x) return null;
        if (x.startsWith("Shopify_GB_")) return x;
        const fromCat = lbShopperApprovedGbFromCatalogString(x);
        if (fromCat) return fromCat;
        // Luigi often sends numeric `product_id` / `id` only (no GID wrapper).
        if (/^\d+$/.test(x) && x.length >= 4 && x.length <= 14) return `Shopify_GB_${x}`;
        const gid = x.match(/Product\/(\d+)/i);
        if (gid && gid[1]) {
            const pidFromGid = gid[1].replace(/\D/g, "");
            if (pidFromGid) return `Shopify_GB_${pidFromGid}`;
        }
        const digits = x.replace(/\D/g, "");
        // Bare numeric Shopify product ids are often 10–13 digits; short ids still valid (never require 8+ after GID handled above).
        return digits.length >= 6 ? `Shopify_GB_${digits}` : null;
    };

    const a = hit.attributes || {};
    let out =
        toClass(a.id) ||
        toClass(a.product_id) ||
        toClass(a.shopify_product_id) ||
        toClass(a.item_group_id) ||
        toClass(hit.id) ||
        toClass(hit.product_id);

    if (!out) {
        for (const n of hit.nested || []) {
            const na = n.attributes || {};
            out =
                toClass(na.id) ||
                toClass(na.product_id) ||
                toClass(na.shopify_product_id);
            if (out) break;
        }
    }

    if (!out) {
        const cands = [
            firstScalar(a.old_identity),
            firstScalar(hit.old_identity),
            firstScalar(a.original_url),
            firstScalar(hit.original_url),
            firstScalar(a.url_en),
            firstScalar(hit.url_en),
            firstScalar(a.url),
            firstScalar(hit.url),
            firstScalar(a.uri),
        ].filter(Boolean);
        for (const u of cands) {
            const us = String(u);
            const fromUrlCat = lbShopperApprovedGbFromCatalogString(us);
            if (fromUrlCat) {
                out = fromUrlCat;
                break;
            }
            if (lbIsGaCatalogIdString(us)) continue;
            const m = us.match(/Product\/(\d+)/i);
            if (m) {
                out = toClass(m[1]);
                break;
            }
        }
    }

    return out;
}

function escapeHtmlAttr(value) {
    return String(value)
        .replace(/&/g, "&amp;")
        .replace(/"/g, "&quot;")
        .replace(/</g, "&lt;");
}

function escapeHtmlText(value) {
    return String(value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
}

/** Strip highlight markup from feed titles (`<em>` etc.) for plain-text card display. */
function lbPlainFeedTitle(raw) {
    if (raw == null || raw === "") return "";
    let s = String(raw);
    if (s.indexOf("<") !== -1) s = s.replace(/<[^>]*>/g, "");
    return s.trim();
}

/** Luigi / feed attribute names for strike-through “was” price (decimal GBP like price_amount). */
const LB_COMPARE_PRICE_KEYS = [
    "compare_at_price",
    "compare_at_price_amount",
    "list_price",
    "price_compare",
    "original_price",
    "compare_price",
];

function lbPickCompare(attrs) {
    if (!attrs) return null;
    for (const key of LB_COMPARE_PRICE_KEYS) {
        const v = firstScalar(attrs[key]);
        if (v != null && v !== "") {
            const n = Number(v);
            if (!Number.isNaN(n)) return n;
        }
    }
    return null;
}

function formatLbMoney(amount) {
    if (amount == null || amount === "") return "";
    const n = Number(amount);
    if (Number.isNaN(n)) return "";
    return `£${n.toFixed(2)}`;
}

/** Custom range uses spaces around `|` (see Search API `f[]` numeric range); Luigi bucket facet values usually omit spaces. */
function isLbCustomPriceRangeFilterValue(v) {
    return /\s\|\s/.test(String(v ?? ""));
}

function formatLbPriceFacetLabelAmount(n) {
    if (!Number.isFinite(n)) return "";
    const rounded = Math.round(n * 100) / 100;
    if (Number.isInteger(rounded) || Math.abs(rounded - Math.round(rounded)) < 0.001) {
        return `£${Math.round(rounded)}`;
    }
    return `£${rounded.toFixed(2)}`;
}

function formatLbPriceBucketLabel(raw) {
    const s = String(raw).trim();
    const parts = s.split("|").map((p) => p.trim());
    const lo = parseFloat(parts[0]);
    const hi = parseFloat(parts[1]);
    if (!Number.isFinite(lo) || !Number.isFinite(hi)) return escapeHtmlText(s);

    const fmt = formatLbPriceFacetLabelAmount;
    if (lo <= 0 || lo < 0.02) {
        return `Under ${fmt(hi)}`;
    }
    if (hi >= 999998) {
        return `${fmt(lo)} or more`;
    }
    return `${fmt(lo)} to ${fmt(hi)}`;
}

function parseLbCustomPriceRangeInputs(minStr, maxStr) {
    let nMin = parseFloat(String(minStr).replace(/,/g, ""));
    let nMax = parseFloat(String(maxStr).replace(/,/g, ""));
    if (!Number.isFinite(nMin)) nMin = 0;
    if (!Number.isFinite(nMax)) nMax = 999999.99;
    if (nMin < 0) nMin = 0;
    if (nMax <= nMin) nMax = nMin + 0.01;
    return { nMin, nMax };
}

function getLbActivePriceCustomRangeStrings(activeVals) {
    return (activeVals || []).filter(isLbCustomPriceRangeFilterValue);
}

function formatLbActiveFilterChipLabel(field, value) {
    if (field !== LB_PRICE_AMOUNT_FACET) {
        const facetLbl = getLbFacetDisplayLabel(field);
        return `${facetLbl}: ${value}`;
    }
    const priceName = getLbFacetDisplayLabel(LB_PRICE_AMOUNT_FACET) || "Price";
    const staticLbl = lbStaticPriceBucketLabelForFilterValue(value);
    if (staticLbl) return `${priceName}: ${staticLbl}`;
    const disp = isLbCustomPriceRangeFilterValue(value)
        ? value
              .split(/\s*\|\s*/)
              .map((x) => formatLbPriceFacetLabelAmount(parseFloat(x.trim())))
              .join(" – ")
        : formatLbPriceBucketLabel(value);
    return `${priceName}: ${disp}`;
}

function lbVariantPriceRows(nested) {
    return (nested || [])
        .filter((n) => n.type === "variant")
        .map((n) => {
            const va = n.attributes || {};
            const pRaw = firstScalar(va.price_amount);
            const p = pRaw != null && pRaw !== "" ? Number(pRaw) : NaN;
            if (Number.isNaN(p)) return null;
            const rawCompare = lbPickCompare(va);
            const tmpC = rawCompare != null && rawCompare > p ? rawCompare : p;
            const onSale = rawCompare != null && rawCompare > p;
            return { p, rawCompare, tmpC, onSale };
        })
        .filter(Boolean);
}

function lbAggregateFromPricingRows(rows) {
    const prices = rows.map((r) => r.p);
    const tmpCs = rows.map((r) => r.tmpC);
    const priceMin = Math.min(...prices);
    const priceMax = Math.max(...prices);
    const tmpCompareMin = Math.min(...tmpCs);
    const tmpCompareMax = Math.max(...tmpCs);
    const onSale = rows.some((r) => r.onSale);
    const strikeCandidates = rows.filter((r) => r.onSale).map((r) => r.rawCompare);
    const strikeCompare = strikeCandidates.length ? Math.min(...strikeCandidates) : null;
    const rawCompared = rows.map((r) => r.rawCompare).filter((c) => c != null);
    const compareVaries =
        rawCompared.length >= 2 &&
        Math.min(...rawCompared) !== Math.max(...rawCompared);

    return {
        priceMin,
        priceMax,
        strikeCompare,
        tmpCompareMin,
        tmpCompareMax,
        priceVaries: priceMin !== priceMax,
        compareVaries,
        onSale,
    };
}

function lbAggregatePricing(a, nested) {
    const rows = lbVariantPriceRows(nested);
    const parentP = firstScalar(a?.price_amount);
    const parentNum = parentP != null && parentP !== "" ? Number(parentP) : NaN;

    if (!rows.length) {
        const p = !Number.isNaN(parentNum) ? parentNum : null;
        const rawC = lbPickCompare(a || {});
        const onSale = p != null && rawC != null && rawC > p;
        const tmpMin = p != null ? (rawC != null ? Math.min(rawC, p) : p) : null;
        const tmpMax = p != null ? (rawC != null ? Math.max(rawC, p) : p) : null;
        return {
            priceMin: p,
            priceMax: p,
            strikeCompare: onSale ? rawC : null,
            tmpCompareMin: tmpMin,
            tmpCompareMax: tmpMax,
            priceVaries: false,
            compareVaries: false,
            onSale: !!onSale,
        };
    }

    return lbAggregateFromPricingRows(rows);
}

/** Mirrors `product-price.liquid` + grid `show_original: true`, `show_range: false`, `include_hidden_price: true`. */
function buildLbProductPriceHtmlFromAggregate(agg, options) {
    const opts = options || {};
    const useMaxForPrimary = !!opts.useMaxForPrimary;
    const str = window.theme && window.theme.strings ? window.theme.strings : {};
    const orig = str.productPriceOriginal || "Original price";
    const curr = str.productPriceCurrent || "Current price";
    const {
        priceMin,
        priceMax,
        strikeCompare,
        tmpCompareMin,
        tmpCompareMax,
        compareVaries,
        onSale,
    } = agg;

    if (priceMin == null || Number.isNaN(priceMin)) {
        return `<div class="price productitem__price"><div class="price__current" data-price-container><span class="money" data-price=""></span></div></div>`;
    }

    const priceVaries = !!agg.priceVaries && priceMax != null && !Number.isNaN(priceMax) && priceMin !== priceMax;
    const primaryCurrent =
        useMaxForPrimary && priceVaries ? priceMax : priceMin;

    const variesClass = compareVaries ? "price--varies" : "";
    const compareVisibleClass = onSale ? "price__compare-at visible" : "price__compare-at";
    const currentClass = `price__current${onSale ? " price__current--on-sale" : ""}`;

    let html = `<div class="price productitem__price ${variesClass}">`;

    html += `<div class="${compareVisibleClass}" data-price-compare-container>`;
    if (onSale && strikeCompare != null) {
        html += `<span class="visually-hidden">${escapeHtmlText(orig)}</span><span class="money price__compare-at--single" data-price-compare>${formatLbMoney(strikeCompare)}</span>`;
    }
    html += `</div>`;

    html += `<div class="price__compare-at--hidden" data-compare-price-range-hidden>`;
    html += `<span class="visually-hidden">${escapeHtmlText(orig)}</span>`;
    html += `<span class="money price__compare-at--min" data-price-compare-min>${formatLbMoney(tmpCompareMin)}</span>`;
    html += ` - <span class="visually-hidden">${escapeHtmlText(orig)}</span>`;
    html += `<span class="money price__compare-at--max" data-price-compare-max>${formatLbMoney(tmpCompareMax)}</span>`;
    html += `</div>`;
    html += `<div class="price__compare-at--hidden" data-compare-price-hidden>`;
    html += `<span class="visually-hidden">${escapeHtmlText(orig)}</span>`;
    html += `<span class="money price__compare-at--single" data-price-compare>${formatLbMoney(strikeCompare != null ? strikeCompare : tmpCompareMin)}</span>`;
    html += `</div>`;

    html += `<div class="${currentClass}" data-price-container>`;
    if (onSale) html += `<span class="visually-hidden">${escapeHtmlText(curr)}</span>`;
    html += `<span class="money" data-price="">${formatLbMoney(primaryCurrent)}</span>`;
    html += `</div>`;

    html += `<div class="price__current--hidden" data-current-price-range-hidden>`;
    html += `<span class="money price__current--min" data-price-min>${formatLbMoney(priceMin)}</span>`;
    html += ` - <span class="money price__current--max" data-price-max>${formatLbMoney(priceMax)}</span>`;
    html += `</div>`;
    html += `<div class="price__current--hidden" data-current-price-hidden>`;
    html += `<span class="visually-hidden">${escapeHtmlText(curr)}</span>`;
    html += `<span class="money" data-price="">${formatLbMoney(primaryCurrent)}</span>`;
    html += `</div>`;

    html += `<div class="productitem__unit-price hidden" data-unit-price><span class="productitem__total-quantity" data-total-quantity=""></span> | <span class="productitem__unit-price--amount money" data-unit-price-amount=""></span> / <span class="productitem__unit-price--measure" data-unit-price-measure=""></span></div>`;

    html += `</div>`;
    return html;
}

function buildLbProductPriceHtml(a, nested) {
    return buildLbProductPriceHtmlFromAggregate(lbAggregatePricing(a, nested), null);
}

function shopifyMoneyToDecimal(v) {
    if (v == null || v === "") return null;
    const s = String(v).trim();
    if (s.includes(".")) {
        const n = Number(s);
        return Number.isNaN(n) ? null : n;
    }
    const n = Number(s);
    if (Number.isNaN(n)) return null;
    return n / 100;
}

function aggregateFromShopifyProduct(product) {
    const variants = product?.variants;
    if (!variants || !variants.length) return null;
    const rows = variants
        .map((v) => {
            const p = shopifyMoneyToDecimal(v.price);
            if (p == null || Number.isNaN(p)) return null;
            const cRaw = shopifyMoneyToDecimal(v.compare_at_price);
            const rawCompare = cRaw != null && cRaw > p ? cRaw : null;
            const tmpC = rawCompare != null ? rawCompare : p;
            return { p, rawCompare, tmpC, onSale: rawCompare != null };
        })
        .filter(Boolean);
    if (!rows.length) return null;
    return lbAggregateFromPricingRows(rows);
}

// Cache: normalized `/products/...` path → `{ id, product }` from `{path}.js` (Shopify AJAX product).
const __lbShopifyProductByPath = new Map();
/** Dedupe concurrent `fetch` for the same path when hydrating many cards in parallel. */
const __lbShopifyProductByPathInflight = new Map();

function normalizeLbProductPath(pathOrUrl) {
    if (pathOrUrl == null || pathOrUrl === "") return null;
    const s = String(pathOrUrl).trim();
    if (!s) return null;
    try {
        const pathOnly = /^https?:/i.test(s) ? new URL(s).pathname : s.split(/[?#]/)[0];
        const basePath = pathOnly.replace(/\/+$/, "");
        if (!/\/products\//i.test(basePath)) return null;
        const slugMatch = basePath.match(/\/products\/([^/?#]+)/i);
        const slug = slugMatch ? slugMatch[1] : "";
        if (
            window.theme &&
            typeof window.theme.lbIsGaCatalogProductSlug === "function" &&
            window.theme.lbIsGaCatalogProductSlug(slug)
        ) {
            return null;
        }
        return basePath;
    } catch {
        return null;
    }
}

function lbIsGaCatalogIdString(str) {
    if (window.theme && typeof window.theme.lbIsGaCatalogIdString === "function") {
        return window.theme.lbIsGaCatalogIdString(str);
    }
    if (str == null || str === "") return false;
    const s = String(str).trim();
    return /^shopify_[A-Za-z]{2}_\d+_\d+$/i.test(s) || /^\/shopify_[A-Za-z]{2}_\d+_\d+$/i.test(s);
}

/** Raw storefront path from Luigi (`old_identity` → `original_url` → `url_en`). */
function lbOldIdentityRaw(hit) {
    if (!hit) return null;
    const a = hit.attributes || {};
    const rawList = [
        firstScalar(hit.old_identity) || firstScalar(a.old_identity),
        firstScalar(hit.original_url) || firstScalar(a.original_url),
        firstScalar(hit.url_en) || firstScalar(a.url_en),
    ];
    for (let ri = 0; ri < rawList.length; ri++) {
        const raw = rawList[ri];
        if (!raw) continue;
        let s = String(raw).trim();
        if (!s) continue;
        if (/^https?:/i.test(s)) {
            try {
                const u = new URL(s);
                return (u.pathname || "") + (u.search || "");
            } catch {
                continue;
            }
        }
        if (!s.startsWith("/")) s = `/${s}`;
        return s;
    }
    return null;
}

/** Normalized `/products/...` path for `.js` fetch (query stripped by normalize). */
function lbStorefrontPathFromOldIdentity(hit) {
    const s = lbOldIdentityRaw(hit);
    if (!s) return null;
    return normalizeLbProductPath(s);
}

/**
 * Canonical `/products/...` path for Shopify `.js` fetch + prefetch maps.
 * Prefer `old_identity` / `original_url` / `url_en`, then `url` / `uri` when not GA catalog strings.
 */
function lbHitProductPathForFetch(hit) {
    if (!hit) return null;
    const fromLegacy = lbStorefrontPathFromOldIdentity(hit);
    if (fromLegacy) return fromLegacy;

    const a = hit.attributes || {};
    const candidates = [];
    const pushCand = (v) => {
        const x = firstScalar(v);
        if (x == null || x === "") return;
        const s = String(x).trim();
        if (!s || lbIsGaCatalogIdString(s)) return;
        candidates.push(s);
    };
    pushCand(a.url);
    pushCand(a.uri);
    pushCand(hit.url);

    for (let ci = 0; ci < candidates.length; ci++) {
        let raw = candidates[ci];
        if (!/^https?:\/\//i.test(raw) && !raw.startsWith("/") && /products\//i.test(raw)) {
            raw = `/${raw}`;
        }
        const direct = normalizeLbProductPath(raw);
        if (direct) return direct;
        const s = String(raw).trim();
        if (!/\/products\//i.test(s)) continue;
        try {
            const pathOnly = new URL(s, "https://example.com").pathname;
            const p = normalizeLbProductPath(pathOnly);
            if (p) return p;
        } catch {
            /* continue */
        }
        const p2 = normalizeLbProductPath(s.split(/[?#]/)[0]);
        if (p2) return p2;
    }
    return null;
}

async function fetchShopifyProductByPath(pathOrUrl) {
    const basePath = normalizeLbProductPath(pathOrUrl);
    if (!basePath) return null;

    if (__lbShopifyProductByPath.has(basePath)) {
        return __lbShopifyProductByPath.get(basePath);
    }

    const inflight = __lbShopifyProductByPathInflight.get(basePath);
    if (inflight) return inflight;

    const shop = window.Shopify && window.Shopify.shop;
    if (!shop) return null;

    const jsonUrl = `https://${shop}${basePath}.js`;
    const promise = (async () => {
        try {
            const res = await fetch(jsonUrl);
            if (!res.ok) return null;
            const product = await res.json();
            const entry = {
                id: product && product.id != null ? product.id : null,
                product,
            };
            __lbShopifyProductByPath.set(basePath, entry);
            return entry;
        } catch (e) {
            console.warn("[LB] product .js failed", jsonUrl, e);
            return null;
        } finally {
            __lbShopifyProductByPathInflight.delete(basePath);
        }
    })();
    __lbShopifyProductByPathInflight.set(basePath, promise);
    return promise;
}

async function resolveShopifyProductIdByPath(pathOrUrl) {
    const ent = await fetchShopifyProductByPath(pathOrUrl);
    return ent && ent.id != null ? ent.id : null;
}

const LB_BADGE_SAVINGS_PH = "@@LB_SAVINGS@@";

function lbShopifySelectedOrFirstVariant(product) {
    const vs = product?.variants;
    if (!vs || !vs.length) return null;
    const avail = vs.find((v) => v.available !== false);
    return avail || vs[0];
}

/** `featured_image` / `images[0]` from Shopify `/products/{handle}.js` (matches `featured_media` on PLP). */
function lbShopifyFeaturedImageUrl(product) {
    if (!product) return null;
    const fi = product.featured_image;
    if (typeof fi === "string" && fi.trim()) return fi.trim();
    if (fi && typeof fi === "object" && fi.src) {
        const u = String(fi.src).trim();
        return u || null;
    }
    const imgs = product.images;
    if (!Array.isArray(imgs) || !imgs.length) return null;
    const first = imgs[0];
    if (typeof first === "string" && first.trim()) return first.trim();
    if (first && typeof first === "object" && first.src) {
        const u = String(first.src).trim();
        return u || null;
    }
    return null;
}

/**
 * `custom.outdoor_image` from product JSON if exposed (Admin → metafield → Storefront access).
 * Matches `product-grid-item.liquid` outdoor grid branch.
 * Unwraps common JSON shapes (`{ value }`, file `reference`, nested `image.url`).
 */
function lbShopifyOutdoorImageUrlFromProductJson(product) {
    if (!product) return null;
    const meta = product.metafields;
    if (!meta || typeof meta !== "object") return null;

    function unwrapOutdoorRaw(raw) {
        if (raw == null) return null;
        if (typeof raw === "string") {
            const t = raw.trim();
            if (!t || /^gid:\/\//i.test(t)) return null;
            return lbScalarImageUrl(raw);
        }
        if (typeof raw === "object") {
            const v = raw.value != null ? raw.value : raw;
            if (v !== raw) return unwrapOutdoorRaw(v);
            const ref = raw.reference;
            if (ref && typeof ref === "object") {
                const img = ref.image;
                if (img && typeof img === "object") {
                    const u = img.url || img.src;
                    if (u) return lbScalarImageUrl(u);
                }
                const u2 = ref.url || ref.src;
                if (u2) return lbScalarImageUrl(u2);
            }
            return lbScalarImageUrl(raw);
        }
        return lbScalarImageUrl(raw);
    }

    function lbAcceptShopifyImageUrlString(s) {
        const t = String(s).trim();
        if (!t || /^gid:\/\//i.test(t)) return false;
        if (/^https?:\/\//i.test(t) || t.startsWith("//")) return true;
        if (t.startsWith("/cdn/")) return true;
        return false;
    }

    const custom = meta.custom;
    if (custom && custom.outdoor_image != null) {
        const u = unwrapOutdoorRaw(custom.outdoor_image);
        if (u) {
            const s = String(u).trim();
            if (lbAcceptShopifyImageUrlString(s)) return s;
        }
    }
    const flat = meta["custom.outdoor_image"];
    if (flat != null) {
        const u = unwrapOutdoorRaw(flat);
        if (u) {
            const s = String(u).trim();
            if (lbAcceptShopifyImageUrlString(s)) return s;
        }
    }
    return null;
}

/** Variant image from Shopify `/products/*.js` (`featured_image`, `featured_media`, or `image_id` lookup). */
function lbShopifyVariantFeaturedImageSrc(variant, product) {
    if (!variant) return null;
    const fi = variant.featured_image;
    if (fi && typeof fi === "object" && fi.src) return String(fi.src).trim() || null;
    if (typeof fi === "string" && fi.trim()) return fi.trim();

    const fm = variant.featured_media;
    if (fm && typeof fm === "object") {
        const preview = fm.preview_image;
        if (preview && typeof preview === "object" && preview.src) {
            const u = String(preview.src).trim();
            if (u) return u;
        }
        if (typeof fm.src === "string" && fm.src.trim()) return fm.src.trim();
    }

    const imageId = variant.image_id != null ? String(variant.image_id).trim() : "";
    if (imageId && product && Array.isArray(product.images)) {
        const matching = product.images.find((img) => String(img && img.id) === imageId);
        if (matching) {
            if (typeof matching === "string" && matching.trim()) return matching.trim();
            if (matching && typeof matching === "object" && matching.src) {
                const u = String(matching.src).trim();
                if (u) return u;
            }
        }
    }

    return null;
}

/** `variant.metafields.custom.shape` when exposed on product JSON (`{ value }` or string). */
function lbShopifyVariantShapeString(variant) {
    if (!variant) return null;
    const m = variant.metafields;
    if (!m || typeof m !== "object") return null;
    const c = m.custom;
    let raw = c && (c.shape ?? c.Shape);
    if (raw == null) raw = m["custom.shape"];
    if (raw == null) return null;
    if (typeof raw === "object" && raw.value != null) raw = raw.value;
    return lbScalarImageUrl(raw) || String(raw).trim() || null;
}

function lbVariantShapeMatchesPlpMode(shapeStr, mode) {
    if (shapeStr == null || shapeStr === "" || !mode) return false;
    const sl = String(shapeStr).trim().toLowerCase();
    if (mode === "runner") {
        return sl === "runner" || sl.includes("runner") || sl.includes("rectangle");
    }
    if (mode === "round") {
        return (
            sl === "round" ||
            sl === "circle" ||
            sl.includes("circle") ||
            sl.includes("round")
        );
    }
    return false;
}

/**
 * Shape-led PLP (runner / round collections): variant image from `custom.shape` + `featured_image` on `/products/*.js`.
 * Fallback: title/options contain runner / round|circle when metafields missing from JSON.
 */
function lbShopifyShapeCollectionVariantImageUrl(product) {
    const mode = lbActiveShapeVariantMode();
    const vs = product?.variants;
    if (!mode || !Array.isArray(vs) || !vs.length) return null;
    for (const v of vs) {
        const shape = lbShopifyVariantShapeString(v);
        if (lbVariantShapeMatchesPlpMode(shape, mode)) {
            const u = lbShopifyVariantFeaturedImageSrc(v, product);
            if (u) return u;
        }
    }
    for (const v of vs) {
        const title = String(v.title || "").toLowerCase();
        const o1 = String(v.option1 || "").toLowerCase();
        const o2 = String(v.option2 || "").toLowerCase();
        const o3 = String(v.option3 || "").toLowerCase();
        if (mode === "runner") {
            if (
                title.includes("runner") ||
                o1.includes("runner") ||
                o2.includes("runner") ||
                o3.includes("runner")
            ) {
                const u = lbShopifyVariantFeaturedImageSrc(v, product);
                if (u) return u;
            }
        } else if (mode === "round") {
            if (
                title.includes("round") ||
                title.includes("circle") ||
                o1.includes("round") ||
                o1.includes("circle") ||
                o2.includes("round") ||
                o2.includes("circle") ||
                o3.includes("round") ||
                o3.includes("circle")
            ) {
                const u = lbShopifyVariantFeaturedImageSrc(v, product);
                if (u) return u;
            }
        }
    }
    return null;
}

/** Same rules as `product-grid-item.liquid`: outdoor metafield, then runner variant image, else featured. */
function lbShopifyPlpCardImageUrl(product) {
    if (!product) return null;
    if (lbShouldUseOutdoorImage()) {
        const outdoor = lbShopifyOutdoorImageUrlFromProductJson(product);
        if (outdoor) return outdoor;
    }
    if (lbActiveShapeVariantMode()) {
        const shapeImg = lbShopifyShapeCollectionVariantImageUrl(product);
        if (shapeImg) return shapeImg;
    }
    return lbShopifyFeaturedImageUrl(product);
}

function lbNormalizedUrlForCompare(input) {
    if (!input) return "";
    try {
        const u = new URL(String(input), window.location.origin);
        return `${u.origin}${u.pathname}`.toLowerCase();
    } catch (_) {
        return String(input).split(/[?#]/)[0].toLowerCase();
    }
}

function lbSameImageUrl(a, b) {
    const aa = lbNormalizedUrlForCompare(a);
    const bb = lbNormalizedUrlForCompare(b);
    return aa !== "" && aa === bb;
}

function hydrateLbCardPrimaryImageFromShopifyProduct(card, product) {
    const img = card.querySelector("img.productitem--image-primary");
    if (!img) return;
    const currentSrc = img.getAttribute("src") || img.currentSrc || "";
    if (lbShouldUseOutdoorImage()) {
        const outdoor = lbShopifyOutdoorImageUrlFromProductJson(product);
        if (outdoor) {
            if (lbSameImageUrl(currentSrc, outdoor)) return;
            img.src = outdoor;
            return;
        }
        /*
         * Room/outdoor filter (or outdoor collection) but `/products/*.js` often omits `metafields.custom.outdoor_image`.
         * First paint already used Luigi `outdoor_image` when the feed has it — do not fall through to featured
         * (that caused a flash: correct outdoor → wrong hero). Only continue when shape-led PLP still needs a
         * variant image from JSON.
         */
        if (!lbActiveShapeVariantMode()) return;
    }
    const url = lbShopifyPlpCardImageUrl(product);
    if (!url) return;
    if (lbSameImageUrl(currentSrc, url)) return;
    img.src = url;
}

/** Match Liquid `{{ product.title }}` on grid cards (Luigi feed titles are often variant-long). */
function hydrateLbCardTitleFromShopifyProduct(card, product) {
    if (!product || product.title == null) return;
    const t = String(product.title).trim();
    if (!t) return;
    const titleP = card.querySelector(".productitem__title > p.mt-0");
    if (titleP) titleP.textContent = t;
    const link = card.querySelector("a.productitem--image-link");
    if (link) link.setAttribute("aria-label", `${t}, opens in new tab`);
    const img = card.querySelector("img.productitem--image-primary");
    if (img) img.setAttribute("alt", t);
}

/** Mirrors `snippets/product-badge.liquid` savings + varies flags (Shopify `/products/*.js` payload). */
function computeLbSaleBadgeState(product) {
    if (!product) return null;
    const str = window.theme && window.theme.strings ? window.theme.strings : {};
    const t = window.theme || {};

    if (product.available === false) {
        return { kind: "soldout", label: str.productBadgeSoldOut || "Sold out" };
    }

    if (t.productSalesBadge === false) return null;

    const variants = product.variants || [];
    let savingsMoneyRange = 0;
    let savingsPercentageRange = 0;

    for (const variant of variants) {
        const p = shopifyMoneyToDecimal(variant.price);
        if (p == null || Number.isNaN(p)) continue;
        const cAt = shopifyMoneyToDecimal(variant.compare_at_price);
        const tmpCompare = cAt != null && cAt > p ? cAt : p;
        const priceDiff = tmpCompare - p;
        if (cAt != null && cAt > p && priceDiff > savingsMoneyRange) {
            savingsMoneyRange = priceDiff;
            savingsPercentageRange = Math.round((priceDiff * 100) / cAt);
        }
    }

    if (savingsMoneyRange <= 0) return null;

    const sel = lbShopifySelectedOrFirstVariant(product);
    let savingsMoneySingle = 0;
    let savingsPercentageSingle = 0;
    if (sel) {
        const sp = shopifyMoneyToDecimal(sel.price);
        const sc = shopifyMoneyToDecimal(sel.compare_at_price);
        if (sp != null && sc != null && sc > sp) {
            savingsMoneySingle = sc - sp;
            savingsPercentageSingle = Math.round((savingsMoneySingle * 100) / sc);
        }
    }

    const prices = variants
        .map((v) => shopifyMoneyToDecimal(v.price))
        .filter((x) => x != null && !Number.isNaN(x));
    const priceVaries = prices.length >= 2 && Math.min(...prices) !== Math.max(...prices);

    const tmpCompares = variants
        .map((v) => {
            const p = shopifyMoneyToDecimal(v.price);
            if (p == null || Number.isNaN(p)) return null;
            const c = shopifyMoneyToDecimal(v.compare_at_price);
            return c != null && c > p ? c : p;
        })
        .filter((x) => x != null && !Number.isNaN(x));
    const compareVaries =
        tmpCompares.length >= 2 && Math.min(...tmpCompares) !== Math.max(...tmpCompares);

    return {
        kind: "sale",
        style: t.productSalesBadgeStyle || "percent",
        savingsMoneyRange,
        savingsPercentageRange,
        savingsMoneySingle,
        savingsPercentageSingle,
        priceVaries,
        compareVaries,
    };
}

function buildLbProductSaleBadgeHtmlFromState(state) {
    if (!state) return "";
    if (state.kind === "soldout") {
        return `<span class="productitem__badge productitem__badge--soldout">${escapeHtmlText(state.label || "Sold out")}</span>`;
    }
    if (state.kind !== "sale") return "";

    const str = window.theme && window.theme.strings ? window.theme.strings : {};
    const style = state.style || "percent";
    const varies = !!(state.priceVaries || state.compareVaries);
    let rangeInner = "";
    let singleInner = "";

    if (style === "percent") {
        const pctR = state.savingsPercentageRange;
        const tplRange = varies ? str.productBadgeSalePctRange : str.productBadgeSalePctSingle;
        rangeInner = (tplRange || "").split(LB_BADGE_SAVINGS_PH).join(String(pctR));
        singleInner = (str.productBadgeSalePctSingle || "")
            .split(LB_BADGE_SAVINGS_PH)
            .join(String(state.savingsPercentageSingle));
    } else if (style === "money") {
        const mr = formatLbMoney(state.savingsMoneyRange);
        const tplMoney = varies ? str.productBadgeSaleMoneyRange : str.productBadgeSaleMoneySingle;
        rangeInner = (tplMoney || "").split(LB_BADGE_SAVINGS_PH).join(mr);
        singleInner = (str.productBadgeSaleMoneySingle || "")
            .split(LB_BADGE_SAVINGS_PH)
            .join(formatLbMoney(state.savingsMoneySingle));
    } else {
        const saleTxt = escapeHtmlText(str.productBadgeSale || "Sale");
        rangeInner = saleTxt;
        singleInner = saleTxt;
    }

    return `<span class="productitem__badge productitem__badge--sale" data-badge-sales><span data-badge-sales-range>${rangeInner}</span><span data-badge-sales-single style="display: none;">${singleInner}</span></span>`;
}

function injectLbProductBadge(card, product) {
    const figure = card.querySelector("figure.productitem--image");
    if (!figure) return;
    figure.querySelectorAll(".productitem__badge").forEach((el) => el.remove());
    const state = computeLbSaleBadgeState(product);
    const html = buildLbProductSaleBadgeHtmlFromState(state);
    if (!html) return;
    const temp = document.createElement("div");
    temp.innerHTML = html.trim();
    const node = temp.firstElementChild;
    if (!node) return;
    const hidden = figure.querySelector(".visually-hidden");
    if (hidden) figure.insertBefore(node, hidden);
    else figure.appendChild(node);
}

/**
 * After Luigi render: primary image + prices from Shopify `/products/*.js`
 * (featured / outdoor metafield per `product-grid-item.liquid`; Luigi `image_link` is first-paint only).
 */
async function hydrateLbCardPricesFromShopify(root, hydrationGen) {
    if (!root) return;
    const cards = Array.from(root.querySelectorAll(".lb-product-card"));
    const jobs = [];
    for (const card of cards) {
        const link = card.querySelector("a[data-product-url]");
        if (!link) continue;
        const url = link.getAttribute("data-product-url");
        if (!url) continue;
        jobs.push({ card, url });
    }

    const fetched = await Promise.all(
        jobs.map(({ card, url }) =>
            fetchShopifyProductByPath(url).then((ent) => ({ card, ent }))
        )
    );

    if (hydrationGen != null && hydrationGen !== lbResultsHydrationGen) return;

    for (const { card, ent } of fetched) {
        if (!ent || !ent.product) continue;
        const product = ent.product;
        const link = card.querySelector("a[data-product-url]");
        if (!link) continue;
        const variant = lbShopifySelectedOrFirstVariant(product);
        const gaItemId =
            window.theme &&
            typeof window.theme.gaCanonicalItemId === "function" &&
            window.theme.gaCanonicalItemId(product.id, variant?.id);
        if (
            gaItemId &&
            window.theme.lbIsCanonicalGaItemId &&
            window.theme.lbIsCanonicalGaItemId(gaItemId)
        ) {
            link.setAttribute("data-item-id", gaItemId);
        }
        hydrateLbCardPrimaryImageFromShopifyProduct(card, product);
        hydrateLbCardTitleFromShopifyProduct(card, product);
        const agg = aggregateFromShopifyProduct(product);
        if (agg && agg.priceMin != null) {
            const priceEl = card.querySelector(".price.productitem__price");
            const useMaxPrimary =
                getSelectedLuigiSort() === "price_amount:desc" && !!agg.priceVaries;
            if (priceEl) {
                const labelP = card.querySelector(".productitem__price_container > p.text-scarpaBlack");
                if (labelP && window.theme.strings) {
                    labelP.textContent = useMaxPrimary
                        ? window.theme.strings.lbUpToLabel || window.theme.strings.lbFromLabel
                        : window.theme.strings.lbFromLabel;
                }
                priceEl.outerHTML = buildLbProductPriceHtmlFromAggregate(agg, { useMaxForPrimary: useMaxPrimary });
            }
            card.classList.toggle("productitem--sale", !!agg.onSale);
        }
        injectLbProductBadge(card, product);
    }
}

/** Swap `.lb-sa-stars-pending` → `.star_container.Shopify_GB_*` (never use `star_container` on pending nodes — SA group2.0 misparses duplicate tokens). */
async function hydratePendingShopperApprovedStars(root, hydrationGen) {
    if (!root || !lbShowShopperApprovedStars()) return;

    const pending = root.querySelectorAll(".lb-sa-stars-pending[data-lb-product-path]");
    if (!pending.length) return;

    const pathToDivs = new Map();
    pending.forEach((div) => {
        const p = div.getAttribute("data-lb-product-path");
        if (!p) return;
        if (!pathToDivs.has(p)) pathToDivs.set(p, []);
        pathToDivs.get(p).push(div);
    });

    for (const [path, divs] of pathToDivs) {
        if (hydrationGen != null && hydrationGen !== lbResultsHydrationGen) return;
        const id = await resolveShopifyProductIdByPath(path);
        if (hydrationGen != null && hydrationGen !== lbResultsHydrationGen) return;
        if (id == null) continue;
        const cls = `Shopify_GB_${id}`;
        divs.forEach((div) => {
            const next = document.createElement("div");
            next.className = `star_container ${cls}`;
            div.replaceWith(next);
        });
    }
}

// Load Shopper Approved aggregate widget script once per distinct product class under `root`.
function hydrateShopperApprovedStars(root, hydrationGen) {
    if (!root || !lbShowShopperApprovedStars()) return;
    if (hydrationGen != null && hydrationGen !== lbResultsHydrationGen) return;
    const siteId =
        (window.theme && window.theme.shopperApprovedSiteId) || "11419";
    const seen = new Set();
    root.querySelectorAll(".star_container").forEach((div) => {
        const cls = div.className.split(/\s+/).find((c) => c.startsWith("Shopify_GB_"));
        if (!cls || seen.has(cls)) return;
        seen.add(cls);
        const script = document.createElement("script");
        script.src = `https://www.shopperapproved.com/widgets/aggregates/${siteId}/${encodeURIComponent(cls)}.js`;
        script.async = true;
        script.onerror = () =>
            console.warn("[Shopper Approved] aggregate failed to load for", cls);
        (document.head || document.documentElement).appendChild(script);
    });
}

async function getLBResults(query, filters = {}, page = 1, categoryFilter = null, opts = {}) {
    if (lbFetchAbort) {
        try {
            lbFetchAbort.abort();
        } catch (_) {
            /* ignore */
        }
    }
    lbFetchAbort = new AbortController();
    const { signal } = lbFetchAbort;
    const fetchEpoch = ++lbLuigiFetchEpoch;

    const stale = () => fetchEpoch !== lbLuigiFetchEpoch;

    // PLP: `query` is null; Luigi gets `f[]=category:…` + `plp=category`. Search: `q` set, no category filter param from theme.
    const isCollectionPLP = Boolean(categoryFilter && !query);
    const applyPromoSlotSizing =
        isCollectionPLP ||
        lbPlpCollectionSprayConfigured() ||
        lbPlpDiscountPromoMayRender();
    const requestSize = computeLuigiRequestPageSize(applyPromoSlotSizing);
    lastLuigiRequestPageSize = requestSize;
    showSkeletons(requestSize);

    const params = new URLSearchParams({
        tracker_id: RESULTS_TRACKER_ID,
        // Include id-ish fields for SA stars; stock/shape/option_size for card UI. Trim only with Luigi team alignment.
        hit_fields:
            "title,title_common,url,old_identity,original_url,url_en,price_amount,compare_at_price,list_price,image_link,outdoor_image,custom.outdoor_image,custom_outdoor_image,metafields.custom.outdoor_image,Outdoor image,nested,Durham Stock,Oxford Stock,Shape,option_size,id,product_id,item_group_id,compare_at_price_amount,price_compare",
        size: requestSize,
        page: page,
    });

    const facetStr = getLbFacetsQueryParam();
    if (facetStr) params.set("facets", facetStr);

    if (query) params.set("q", query);

    const lbSort = getSelectedLuigiSort();
    if (lbSort) params.set("sort", lbSort);
    params.append("f[]", "type:product");

    if (isCollectionPLP) {
        params.append("f[]", `${categoryFilter.field}:${categoryFilter.value}`);
        params.set("plp", categoryFilter.field);
    }

    for (const [key, values] of Object.entries(filters)) {
        values.forEach(v => params.append("f[]", `${key}:${v}`));
    }

    try {
        const response = await fetch(`${API_ENDPOINT}?${params}`, { signal });
        if (stale()) return;
        const rawText = await response.text();
        if (stale()) return;
        let data;
        try {
            data = JSON.parse(rawText);
        } catch (parseErr) {
            // Proxies / outages sometimes return HTML; log a snippet for debugging.
            console.error("Luigi search: not JSON", response.status, rawText.slice(0, 500));
            throw parseErr;
        }

        if (stale()) return;

        if (!response.ok) {
            if (stale()) return;
            console.error("Luigi search HTTP error", response.status, data);
            resultsContainer.innerHTML = "<p>" + window.theme.strings.lbErrorMessage + "</p>";
            resultsHeading.textContent = "";
            return;
        }

        if (!data || !data.results) {
            if (stale()) return;
            console.error("Luigi search: missing data.results", data);
            resultsContainer.innerHTML = "<p>" + window.theme.strings.lbErrorMessage + "</p>";
            resultsHeading.textContent = "";
            return;
        }

        if (stale()) return;

        const res = data.results;

        const sortKey = getSelectedLuigiSort();
        if (res.hits && res.hits.length) {
            res.hits = stabilizeSortedHits(res.hits, sortKey);
        }

        if (stale()) return;

        currentPage = page;
        const hydrationGen = ++lbResultsHydrationGen;
        renderLbResultsGrid(res);
        renderFacets(res.facets);
        renderActiveFilterOptions(res.filters);
        renderPagination(res.total_hits, requestSize);
        updateURL(query, filters, page);

        if (isCollectionPLP) {
            trackListView(res.hits, categoryFilter.value, categoryFilter.identity, activeFilters);
        } else {
            trackSearchView(query, res.hits, activeFilters);
        }

        void hydrateLbResultsGridAfterPaint(resultsContainer, hydrationGen);

        if (opts.scrollToGrid) {
            scrollLbResultsIntoView();
        }
    } catch (err) {
        if (err && err.name === "AbortError") return;
        if (fetchEpoch !== lbLuigiFetchEpoch) return;
        console.error("Error fetching LB results:", err);
        resultsContainer.innerHTML = "<p>" + window.theme.strings.lbErrorMessage + "</p>";
        resultsHeading.textContent = "";
    }
}

// `static-collection.liquid`: discount promo at forloop.index === 3; style 3 when `paginate.current_page % 2 == 0`, else style 2.
function getLbCollectionDiscountPromoHtml() {
    if (
        !document.querySelector("[data-lb-collection]") &&
        !document.querySelector("[data-lb-search]")
    ) {
        return "";
    }
    if (!window.theme || !window.theme.lbDiscountPromoEnabled) return "";
    const useStyle3 = currentPage % 2 === 0;
    const preferred = useStyle3
        ? ["lb-plp-discount-slot-3", "lb-plp-discount-slot-2", "lb-plp-discount-slot-1"]
        : ["lb-plp-discount-slot-2", "lb-plp-discount-slot-3", "lb-plp-discount-slot-1"];
    for (const id of preferred) {
        const tmpl = document.getElementById(id);
        if (tmpl && tmpl.innerHTML && tmpl.innerHTML.trim()) return tmpl.innerHTML.trim();
    }
    return "";
}

function initLbDiscountCopyButtons(root) {
    if (!root) return;
    root.querySelectorAll("[data-discount-copy]").forEach((elem) => {
        elem.addEventListener("click", (e) => {
            e.preventDefault();
            const code = elem.dataset.discountCopy || "";
            if (navigator.clipboard && navigator.clipboard.writeText) {
                void navigator.clipboard.writeText(code);
            }
            elem.classList.add("active");
            setTimeout(() => {
                elem.classList.remove("active");
                elem.blur();
            }, 1500);
        });
    });
}

// Placeholder cards while `fetch` is in flight (count matches Luigi `size` for this request).
function showSkeletons(countArg) {
    const count = Math.max(1, parseInt(String(countArg != null ? countArg : RESULTS_PER_PAGE), 10) || 24);
    resultsContainer.innerHTML = Array.from({ length: count }, () => `
        <div class="lb-product-card lb-skeleton-card product-card productgrid--item imagestyle--natural productitem--emphasis">
            <div class="productitem">
                <div class="productitem__wrapper">
                    <div class="lb-skeleton lb-skeleton--image"></div>
                    <div class="productitem__container">
                        <div class="lb-skeleton lb-skeleton--title"></div>
                        <div class="lb-skeleton lb-skeleton--price"></div>
                    </div>
                </div>
            </div>
        </div>
    `).join("");
}

// Build product cards (sync). SA + Shopify `.js` enrichment runs in `hydrateLbResultsGridAfterPaint` so filters are not blocked.
async function hydrateLbResultsGridAfterPaint(root, hydrationGen) {
    if (!root || hydrationGen !== lbResultsHydrationGen) return;
    await hydratePendingShopperApprovedStars(root, hydrationGen);
    if (hydrationGen !== lbResultsHydrationGen) return;
    await hydrateLbCardPricesFromShopify(root, hydrationGen);
    if (hydrationGen !== lbResultsHydrationGen) return;
    hydrateShopperApprovedStars(root, hydrationGen);
}

// Build product cards to match `product-grid-item` styling; wire variant accordion + analytics.
function renderLbResultsGrid(resultsData) {
    if (!resultsData) {
        resultsContainer.innerHTML = "<p>" + window.theme.strings.lbErrorMessage + "</p>";
        return;
    }

    const hits = resultsData.hits || [];
    const totalHits = resultsData.total_hits || 0;

    if (document.querySelector("[data-lb-search]")) {
        const searchedTerm =
            resultsData.query || (searchResultsInput && searchResultsInput.value.trim()) || "";
        resultsHeading.textContent = `${totalHits.toLocaleString()} ` + window.theme.strings.lbBreadcrumbResultsText + ` "${searchedTerm}"`;
    }

    if (!hits.length) {
        resultsContainer.innerHTML = "<p>" + window.theme.strings.lbNoResults + "</p>";
        return;
    }

    closeDrawer();

    const cardHtmlParts = hits.map((hit) => {
        const a = hit.attributes || {};
        const nested = hit.nested || [];
        const luigiTitleRaw = a.title_common || a.title || "";
        const luigiTitlePlain = lbPlainFeedTitle(luigiTitleRaw);
        const pathKey = lbHitProductPathForFetch(hit);
        const trackingItemId = lbItemIdForLuigiHit(hit);
        /** Never fall back to raw `url` — Luigi may send GA catalog ids, which produced `/shopify_US_...` links. */
        const linkPath = pathKey || "";
        const productPathForHref = linkPath ? (linkPath.startsWith("/") ? linkPath : `/${linkPath}`) : "";
        let productUrl = productPathForHref
            ? `https://${window.Shopify.shop}${productPathForHref}`
            : "#";
        if (productUrl === "#" && window.theme && typeof window.theme.lbVariantStorefrontUrlFromGaItemId === "function") {
            const v = window.theme.lbVariantStorefrontUrlFromGaItemId(trackingItemId);
            if (v) productUrl = v;
        }
        const dataProductUrl = pathKey || linkPath;
        const displayTitle = luigiTitlePlain;
        const priceAgg = lbAggregatePricing(a, nested);
        const lbSortForCard = getSelectedLuigiSort();
        const useMaxPrimary = lbSortForCard === "price_amount:desc" && !!priceAgg.priceVaries;
        const priceBlock = buildLbProductPriceHtmlFromAggregate(priceAgg, { useMaxForPrimary: useMaxPrimary });
        const saleClass = priceAgg.onSale ? " productitem--sale" : "";

        const feedOutdoorSrc = lbShouldUseOutdoorImage()
            ? lbOutdoorImageFromAttributes(a, nested)
            : null;
        const feedOutdoorTrimmed = feedOutdoorSrc ? String(feedOutdoorSrc).trim() : "";
        const cardPrimarySrc = feedOutdoorTrimmed || lbCardPrimaryImageSrc(a, nested);
        const defaultGridImg = lbScalarImageUrl(a.image_link) || "";
        const outdoorImg =
            lbLayoutUsesOutdoorPlpImage() &&
            !!feedOutdoorTrimmed &&
            defaultGridImg !== feedOutdoorTrimmed;
        const titleAttr = escapeHtmlAttr(displayTitle);
        const titleText = escapeHtmlText(displayTitle);
        const imgSrcForTag = cardPrimarySrc || LB_GRID_IMAGE_PLACEHOLDER;
        const img = `<img src="${escapeHtmlAttr(imgSrcForTag)}" alt="${titleAttr}" loading="lazy" class="productitem--image-primary"${outdoorImg ? ' width="512" height="512"' : ""} onerror="this.style.visibility='hidden'">`;

        let showVariantDropdown = false;
        let variantDropdownText;
        let nextDayDelivery = false;
        let showBadge = false;
        let badgeIcon;
        let badgeLabel;

        const nestedVariants = nested.filter((n) => n.type === "variant");
        let sizeRows = nestedVariants.map((variant) => ({
            title: variant.attributes?.option_size ? variant.attributes.option_size[0] : "",
            price: variant.attributes?.price_amount != null ? variant.attributes.price_amount : null
        }));
        if (!sizeRows.length) {
            const rootSize = Array.isArray(a.option_size) ? a.option_size[0] : a.option_size;
            const rootSizeText = rootSize != null ? String(rootSize).trim() : "";
            if (rootSizeText) {
                sizeRows = [{
                    title: rootSizeText,
                    price: a.price_amount != null ? a.price_amount : null
                }];
            }
        }
        sizeRows = sortLbSizeRowsForCard(sizeRows);
        const variantCount = sizeRows.length;
        if (variantCount > 0) {
            showVariantDropdown = true;
            variantDropdownText = variantCount + ' ' + window.theme.strings.lbVariantsLabel;
            if (variantCount > 1) variantDropdownText += "s";
        }

        nested.forEach((nestedItem) => {
            const durhamStock = nestedItem.attributes?.["Durham Stock"]?.[0];
            const oxfordStock = nestedItem.attributes?.["Oxford Stock"]?.[0];
            if ((durhamStock !== undefined && durhamStock > 0) || (oxfordStock !== undefined && oxfordStock > 0)) {
                nextDayDelivery = true;
            }

            // Match `product-grid-item.liquid`: variant metafield `custom.shape` → Round (circle icon) or Runner (rectangle).
            // Luigi nested attribute "Shape" must mirror those values; only set badge when we recognize the shape (avoid __undefined).
            const shape = nestedItem.attributes?.["Shape"]?.[0];
            if (shape != null && shape !== "") {
                const sl = String(shape).trim().toLowerCase();
                if (sl.includes("runner") || sl.includes("rectangle")) {
                    showBadge = true;
                    badgeIcon = "rectangle";
                    badgeLabel = "Runner";
                } else if (sl === "round" || sl === "circle" || sl.includes("circle")) {
                    showBadge = true;
                    badgeIcon = "circle";
                    badgeLabel = "Round";
                }
            }
        });

        // Shopper Approved: class from feed when possible; else `.lb-sa-stars-pending` + `/products/*.js` id resolve.
        const showSa = lbShowShopperApprovedStars();
        const saProductClass = showSa ? shopperApprovedClassFromHit(hit) : null;
        const saProductPath = showSa && !saProductClass ? lbHitProductPathForFetch(hit) : null;
        let saStarsHtml = "";
        if (saProductClass) {
            saStarsHtml = `<div class="star_container ${saProductClass}"></div>`;
        } else if (saProductPath) {
            saStarsHtml = `<div class="lb-sa-stars-pending" data-lb-product-path="${escapeHtmlAttr(saProductPath)}"></div>`;
        }

        return `
        <div class="lb-product-card product-card productgrid--item imagestyle--natural productitem--emphasis${saleClass}">
            <div class="productitem" data-product-item-content>
                <div class="productitem__wrapper">
                    <div class="productitem__image-container">
                        <a class="productitem--image-link" href="${productUrl}" data-product-page-link data-product-url="${escapeHtmlAttr(dataProductUrl)}" data-item-id="${escapeHtmlAttr(String(trackingItemId || ""))}" target="_blank" rel="noopener noreferrer" aria-label="${escapeHtmlAttr(`${displayTitle}, opens in new tab`)}">
                            <figure class="productitem--image"> 
                                ${showBadge ? `
                                    <div class="productitem-shapes__container">
                                        <span class="productitem-shapes__${badgeIcon}" aria-hidden="true"></span>
                                        <span class="productitem-shapes__text ff-heading">${badgeLabel} ` + window.theme.strings.lbBadgeText + `</span>
                                    </div>
                            ` : ''}
                                ${img}
                            </figure>
                        </a>
                    </div>
                    <div class="productitem__container">
                        <div class="productitem__title">
                            <p class="mt-0">${titleText}</p>
                                <div class="productitem__price_container">
                                <p class="mt-0 mb-0 text-scarpaBlack">` +
            (useMaxPrimary
                ? (window.theme.strings.lbUpToLabel || window.theme.strings.lbFromLabel)
                : window.theme.strings.lbFromLabel) +
            `</p>
                                <div class="ff-heading break-none">
                                    ${priceBlock}
                                </div>
                            </div>
                        </div>
                        ${saStarsHtml}
                        <div class="productitem__footer">
                            <p class="text-brandPurple">
                            ${nextDayDelivery ? `
                                <span class="icon" aria-hidden="true">
                                    <svg width="14" height="12" viewBox="0 0 14 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M13.3335 8.00002H13.0002V5.74794C13.0002 5.48335 12.8939 5.22919 12.7064 5.04169L10.6252 2.96044C10.4377 2.77294 10.1835 2.66669 9.91891 2.66669H9.00016V1.66669C9.00016 1.1146 8.55225 0.666687 8.00016 0.666687H1.3335C0.781413 0.666687 0.333496 1.1146 0.333496 1.66669V8.33335C0.333496 8.88544 0.781413 9.33335 1.3335 9.33335H1.66683C1.66683 10.4375 2.56266 11.3334 3.66683 11.3334C4.771 11.3334 5.66683 10.4375 5.66683 9.33335H8.3335C8.3335 10.4375 9.22933 11.3334 10.3335 11.3334C11.4377 11.3334 12.3335 10.4375 12.3335 9.33335H13.3335C13.5168 9.33335 13.6668 9.18335 13.6668 9.00002V8.33335C13.6668 8.15002 13.5168 8.00002 13.3335 8.00002ZM3.66683 10.3334C3.11475 10.3334 2.66683 9.88544 2.66683 9.33335C2.66683 8.78127 3.11475 8.33335 3.66683 8.33335C4.21891 8.33335 4.66683 8.78127 4.66683 9.33335C4.66683 9.88544 4.21891 10.3334 3.66683 10.3334ZM10.3335 10.3334C9.78141 10.3334 9.3335 9.88544 9.3335 9.33335C9.3335 8.78127 9.78141 8.33335 10.3335 8.33335C10.8856 8.33335 11.3335 8.78127 11.3335 9.33335C11.3335 9.88544 10.8856 10.3334 10.3335 10.3334ZM12.0002 6.00002H9.00016V3.66669H9.91891L12.0002 5.74794V6.00002Z" fill="#7F187F"/>
                                    </svg>
                                </span>
                                ` + window.theme.strings.lbFreeDelivery + `
                            ` : ''}
                            </p>
                            ${showVariantDropdown ? `
                                <button type="button" data-dropdown-toggle aria-expanded="false">
                                    <span class="ff-heading break-none">
                                        ` + variantDropdownText + `
                                    </span>
                                </button>
                            ` : ''}
                        </div>
                        ${showVariantDropdown ? `
                            <ul class="productitem__collapsible-list" data-dropdown-list>
                                ${sizeRows.map((row) => {
                                    const variantPrice = row.price != null ? `£${row.price}` : "";
                                    const variantTitle = row.title || "";
                                    return `
                                        <li class="productitem__collapsible-list--item">
                                            <p>${variantTitle}</p>
                                            <span class="ff-heading">${variantPrice}</span>
                                        </li>
                                    `;
                                }).join("")}
                            </ul>
                        ` : ''}
                    </div>
                </div>
            </div>
        </div>`;
    });

    const cols = lbProductGridColumnCount();
    const promoHtml = getLbCollectionDiscountPromoHtml();
    const sprayHtml = getLbCollectionSprayHtml();
    const sprayAt = lbCollectionSprayTriggerIndex();
    const sprayBaseOk = Boolean(sprayHtml && sprayAt > 0);
    const canDiscount = Boolean(promoHtml) && hits.length >= 3;

    const { insertDiscount, insertSpray } = lbResolvePlpPromoInserts(
        hits.length,
        canDiscount,
        sprayBaseOk,
        sprayAt
    );

    let discountInserted = false;
    if (insertDiscount) {
        cardHtmlParts.splice(2, 0, promoHtml);
        discountInserted = true;
    }
    if (insertSpray) {
        const idx = lbCollectionSpraySpliceIndex(sprayAt, discountInserted);
        if (idx >= 0 && idx <= cardHtmlParts.length) {
            cardHtmlParts.splice(idx, 0, sprayHtml);
        }
    }

    lbPlpAppendGridRowFillers(cardHtmlParts, cols);

    resultsContainer.innerHTML = cardHtmlParts.join("");

    initLbDiscountCopyButtons(resultsContainer);

    resultsContainer.querySelectorAll("[data-product-page-link]").forEach((link) => {
        link.addEventListener("click", () => trackClickEvent(link.dataset.itemId));
    });

    resultsContainer.querySelectorAll("[data-dropdown-toggle]").forEach((btn) => {
        btn.addEventListener("click", (e) => {
            e.preventDefault();
            const isExpanded = btn.getAttribute('aria-expanded') === 'true';
            btn.setAttribute('aria-expanded', String(!isExpanded));
            btn.classList.toggle('active');
            btn.parentNode.nextElementSibling.classList.toggle('active');
        });
    });
}

function renderLbStaticPriceFacetGroupHtml(minLbl, maxLbl, applyLbl) {
    const name = LB_PRICE_AMOUNT_FACET;
    const activeForFacet = activeFilters[name] || [];

    const customs = getLbActivePriceCustomRangeStrings(activeForFacet);
    const customStr = customs[0];
    let minValAttr = "";
    let maxValAttr = "";
    if (customStr) {
        const parts = customStr.split(/\s*\|\s*/);
        if (parts[0] != null && String(parts[0]).trim() !== "") {
            minValAttr = ` value="${escapeHtmlAttr(String(parts[0]).trim())}"`;
        }
        if (parts[1] != null && String(parts[1]).trim() !== "") {
            maxValAttr = ` value="${escapeHtmlAttr(String(parts[1]).trim())}"`;
        }
    }

    const bucketRows = LB_PRICE_STATIC_BUCKETS.map((b) => {
        const checked = activeForFacet.includes(b.filterValue);
        const anim = checked ? "checked" : "unchecked";
        return `
        <li class="collection-filters__filter-list-item">
          <label class="collection-filters__filter-link"${checked ? " data-filter-active" : ""} tabindex="0">
            <input type="checkbox" class="visually-hidden" tabindex="-1"
              data-facet="${name}"
              data-value="${escapeHtmlAttr(b.filterValue)}"
              ${checked ? "checked" : ""}>
            <span class="filter-icon--checkbox" aria-hidden="true">
              <svg class="checkmark" aria-hidden="true" focusable="false" role="presentation" width="14" height="14" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 14 14" data-animation-state="${anim}">
                <path class="checkmark__check" fill="none" d="M2.5 7L5.5 10L11.5 4" data-animation-state="${anim}"></path>
                <path class="checkmark__indeterminate" fill="none" d="M2.5 7L6 7L12 7" data-animation-state="${anim}"></path>
              </svg>
            </span>
            <span class="collection-filters__filter-list-item-text">${escapeHtmlText(b.label)}</span>
          </label>
        </li>`;
    }).join("");

    const rangeBlock = `
        <div class="lb-price-custom-range" data-lb-price-custom>
          <div class="lb-price-custom-range__inputs">
            <div class="lb-price-custom-range__field">
              <span class="lb-price-custom-range__lbl">${escapeHtmlText(minLbl)}</span>
              <input type="number" class="lb-price-custom-min" min="0" step="0.01" placeholder="0"${minValAttr} inputmode="decimal" aria-label="${escapeHtmlAttr(minLbl)}">
            </div>
            <div class="lb-price-custom-range__field">
              <span class="lb-price-custom-range__lbl">${escapeHtmlText(maxLbl)}</span>
              <input type="number" class="lb-price-custom-max" min="0" step="0.01" placeholder="999.99"${maxValAttr} inputmode="decimal" aria-label="${escapeHtmlAttr(maxLbl)}">
            </div>
          </div>
          <button type="button" class="lb-price-custom-apply">${escapeHtmlText(applyLbl)}</button>
        </div>`;

    const label = getLbFacetDisplayLabel(LB_PRICE_AMOUNT_FACET) || "Price";
    const bucketList = `<ul class="mt-0 pl-0 lb-price-bucket-list">${bucketRows}</ul>`;

    return `
        <div class="lb-filter-filters__group lb-filter-filters__group--price collection-filters__filter-group">
          <button
            class="lb-filter-filters__toggle collection-filters__filter-group-heading productgrid--sidebar-button"
            type="button"
            data-group="${name}"
            aria-expanded="false"
          >
            <div class="collection-filters__filter-title">${label}</div>
            <div class="collection-filters__filter-icon-wrapper" tabindex="-1">
              <span class="collection-filters__filter-icon">
                <svg aria-hidden="true" focusable="false" role="presentation" width="8" height="6" viewBox="0 0 8 6" fill="none" xmlns="http://www.w3.org/2000/svg" class="icon-chevron-down">
                  <path class="icon-chevron-down-left" d="M4 4.5L7 1.5" stroke="currentColor" stroke-width="1.25" stroke-linecap="square"></path>
                  <path class="icon-chevron-down-right" d="M4 4.5L1 1.5" stroke="currentColor" stroke-width="1.25" stroke-linecap="square"></path>
                </svg>
              </span>
            </div>
          </button>
          <div class="lb-filter-filters__options lb-filter-filters__options--price">${bucketList}${rangeBlock}</div>
        </div>`;
}

/** Matches `snippets/icon-checkmark-animated.liquid` (theme hover/focus on `.collection-filters__filter-link`). */
function lbAnimatedCheckmarkSvg(animationState, additionalClasses = "") {
  const extra = additionalClasses ? ` ${additionalClasses}` : "";
  return `<svg class="checkmark${extra}" aria-hidden="true" focusable="false" role="presentation" width="14" height="14" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 14 14" data-animation-state="${animationState}">
  <path class="checkmark__check" fill="none" d="M2.5 7L5.5 10L11.5 4" data-animation-state="${animationState}"></path>
  <path class="checkmark__indeterminate" fill="none" d="M2.5 7L6 7L12 7" data-animation-state="${animationState}"></path>
</svg>`;
}

let lbFacetBarClickAwayBound = false;

/** Close every facet group in `host`, or every group except `exceptGroup` (sibling dropdowns when focus moves). */
function lbCloseFacetGroupsExcept(host, exceptGroup) {
  if (!host) return;
  host.querySelectorAll(".lb-filter-filters__group").forEach((g) => {
    if (exceptGroup && g === exceptGroup) return;
    g.classList.remove("is-open");
    const tb = g.querySelector(".lb-filter-filters__toggle");
    if (tb) {
      tb.setAttribute("aria-expanded", "false");
      tb.classList.remove("productgrid--sidebar-button-active");
    }
    const colourUl = g.querySelector("ul.collection-filters__filter-colours[data-accordion-content]");
    if (colourUl) {
      colourUl.classList.remove("filter-group-active");
      colourUl.dataset.accordionState = "closed";
    }
  });
}

function closeLbOpenFacetDropdownsIn(host) {
  lbCloseFacetGroupsExcept(host, null);
}

let lbFacetFocusManagementBound = false;

/** Tab/focus: close flydowns when leaving the facet host, or close other groups when focus enters a different group (mega-menu style). */
function ensureLbFacetFocusManagement() {
  if (lbFacetFocusManagementBound) return;
  lbFacetFocusManagementBound = true;
  document.addEventListener(
    "focusin",
    (e) => {
      const t = e.target;
      if (!t || !(t instanceof Element)) return;

      const facetsHost = document.getElementById("lb-facets-container");
      const drawerHost = document.getElementById("lb-filter-drawer-body");

      [facetsHost, drawerHost].forEach((host) => {
        if (!host) return;
        if (!host.contains(t)) {
          lbCloseFacetGroupsExcept(host, null);
          return;
        }
        const group = t.closest(".lb-filter-filters__group");
        if (!group || !host.contains(group)) {
          lbCloseFacetGroupsExcept(host, null);
          return;
        }
        lbCloseFacetGroupsExcept(host, group);
      });
    },
    true
  );
}

/** Visually-hidden checkboxes are a poor Tab target; keep them off the tab order and activate via the label (Enter/Space). */
function wireLbFacetFilterLabelKeyboard(container) {
  if (!container) return;
  container.querySelectorAll("label.collection-filters__filter-link").forEach((label) => {
    const inp = label.querySelector('input[type="checkbox"]');
    if (!inp) return;
    label.addEventListener("keydown", (ev) => {
      if (inp.disabled) return;
      if (ev.key !== "Enter" && ev.key !== " ") return;
      ev.preventDefault();
      inp.click();
    });
  });
}

/** Desktop: close colour + `.lb-filter-filters__options` dropdowns on outside click (`collection-grid-filters.liquid`). */
function ensureLbFacetBarClickAway() {
  if (lbFacetBarClickAwayBound) return;
  lbFacetBarClickAwayBound = true;
  document.addEventListener("click", (e) => {
    if (window.matchMedia("(max-width: 859px)").matches) return;
    const host = document.getElementById("lb-facets-container");
    if (!host) return;
    const t = e.target;
    if (t.closest("#lb-facets-container .lb-filter-filters__group--colours")) return;
    if (t.closest("#lb-facets-container .lb-filter-filters__group.is-open")) return;
    closeLbOpenFacetDropdownsIn(host);
  });
}

// Duplicate facet markup into `#lb-facets-container` (desktop) and drawer body (mobile); same handlers on both.
function renderFacets(facetsData = []) {
  const str = window.theme && window.theme.strings ? window.theme.strings : {};
  const minLbl = str.lbPriceFilterMin || "Min (£)";
  const maxLbl = str.lbPriceFilterMax || "Max (£)";
  const applyLbl = str.lbPriceFilterApply || "Apply";

  const hasPriceFacet = facetsData.some((f) => f.name === LB_PRICE_AMOUNT_FACET);
  const priceGroupFallback = !hasPriceFacet ? renderLbStaticPriceFacetGroupHtml(minLbl, maxLbl, applyLbl) : "";

  const html = `<div class="lb-filters-container">` +
    priceGroupFallback +
    facetsData.map(facet => {
      const name = facet.name;

      if (name === LB_PRICE_AMOUNT_FACET) {
        return renderLbStaticPriceFacetGroupHtml(minLbl, maxLbl, applyLbl);
      }

      const activeForFacetRaw = activeFilters[name] || [];
      const valuesAll = facet.values || [];
      const isColourFacet = LB_COLOUR_FACET_NAMES.has(name);
      const valuesForFacetBase = normalizeLbFacetOptionRows(valuesAll);
      const valuesForFacet = orderLbFacetValues(name, valuesForFacetBase);
      const values =
          isColourFacet || lbFacetUsesFullValueList(name)
              ? valuesForFacet
              : valuesForFacet.slice(0, LB_FACET_VALUES_PREVIEW_CAP);
      if (!values.length) return "";

      const label = getLbFacetDisplayLabel(name);

      const chevronBtnInner = `
            <div class="collection-filters__filter-title">${label}</div>
            <div class="collection-filters__filter-icon-wrapper" tabindex="-1">
              <span class="collection-filters__filter-icon">
                <svg aria-hidden="true" focusable="false" role="presentation" width="8" height="6" viewBox="0 0 8 6" fill="none" xmlns="http://www.w3.org/2000/svg" class="icon-chevron-down">
                  <path class="icon-chevron-down-left" d="M4 4.5L7 1.5" stroke="currentColor" stroke-width="1.25" stroke-linecap="square"></path>
                  <path class="icon-chevron-down-right" d="M4 4.5L1 1.5" stroke="currentColor" stroke-width="1.25" stroke-linecap="square"></path>
                </svg>
              </span>
            </div>`;

      if (isColourFacet) {
        return `
        <div class="lb-filter-filters__group collection-filters__filter-group lb-filter-filters__group--colours" data-filter-group>
          <button
            class="lb-filter-filters__toggle collection-filters__filter-group-heading productgrid--sidebar-button"
            type="button"
            data-filter-group-trigger
            data-group="${name}"
            aria-expanded="false"
          >${chevronBtnInner}
          </button>
          <ul
            class="mt-0 pl-0 collection-filters__filter collection-filters__filter-colours collection-filters__filter-list"
            data-filter-group-list
            data-accordion-content
            data-accordion-animation
            data-accordion-state="closed"
          >
            ${renderLbColourFacetOptions(name, values, activeForFacetRaw)}
          </ul>
        </div>`;
      }

      const handleSlug = label.replace(/\s+/g, "-");
      const options = `<ul class="mt-0 mb-0 pl-0 collection-filters__filter-list">
${values
          .map((v, idx) => {
            const checked     = lbActiveFacetListIncludesMergeKey(activeForFacetRaw, v.value);
            const animState   = checked ? "checked" : "unchecked";
            const count       = v.hits_count ?? 0;
            const disabled    = count === 0 && !checked;
            const activeAttr  = checked ? " data-filter-active" : "";
            const disabledAttr = disabled ? " data-disabled" : "";
            const inpDisabled = disabled ? " disabled" : "";
            const markClasses = disabled ? "checkmark--disabled" : "";
            const dataHandle  = `filter-${handleSlug}-${idx}`;
            const labelTab = disabled ? ' tabindex="-1"' : ' tabindex="0"';
            return `        <li class="collection-filters__filter-list-item" data-filter-open="false">
          <label class="collection-filters__filter-link"${activeAttr}${disabledAttr}${labelTab}>
            <input type="checkbox" class="visually-hidden" tabindex="-1"
              data-facet="${escapeHtmlAttr(name)}"
              data-value="${escapeHtmlAttr(v.value)}"
              ${checked ? "checked" : ""}${inpDisabled}>
            <span class="filter-icon--checkbox" data-handle="${escapeHtmlAttr(dataHandle)}">
              ${lbAnimatedCheckmarkSvg(animState, markClasses)}
            </span>
            <span class="collection-filters__filter-list-item-text">${escapeHtmlText(v.value)} <span class="text-doveGray">(${count})</span></span>
          </label>
        </li>`;
          })
          .join("\n")}
      </ul>`;

      return `
        <div class="lb-filter-filters__group collection-filters__filter-group">
          <button
            class="lb-filter-filters__toggle collection-filters__filter-group-heading productgrid--sidebar-button"
            type="button"
            data-group="${name}"
            aria-expanded="false"
          >${chevronBtnInner}
          </button>
          <div class="lb-filter-filters__options">${options}</div>
        </div>`;
    }).join("") +
  `</div>`;

  facetsContainer.innerHTML = html;
  drawerBody.innerHTML      = html;

  ensureLbFacetBarClickAway();
  ensureLbFacetFocusManagement();

  [facetsContainer, drawerBody].forEach(container => {

    container.querySelectorAll("input[type=checkbox]").forEach(cb => {
      cb.addEventListener("change", handleFacetChange);
    });

    wireLbFacetFilterLabelKeyboard(container);

    wireLbPriceFacetControls(container);

    container.querySelectorAll(".lb-filter-filters__toggle").forEach(btn => {
      btn.addEventListener("click", (e) => {
        const panel = btn.nextElementSibling;
        const isColourPanel = panel?.matches?.(
          "ul.collection-filters__filter-colours[data-accordion-content]"
        );

        if (isColourPanel) {
          e.stopPropagation();
          const isOpen =
            panel.classList.contains("filter-group-active") ||
            panel.dataset.accordionState === "open";

          container.querySelectorAll(".lb-filter-filters__group.is-open").forEach(g => {
            g.classList.remove("is-open");
            const t = g.querySelector(".lb-filter-filters__toggle");
            t?.setAttribute("aria-expanded", "false");
            t?.classList.remove("productgrid--sidebar-button-active");
          });
          container
            .querySelectorAll("ul.collection-filters__filter-colours[data-accordion-content]")
            .forEach(ul => {
              if (ul === panel) return;
              ul.classList.remove("filter-group-active");
              ul.dataset.accordionState = "closed";
              const t = ul.previousElementSibling;
              if (t?.matches?.("button.lb-filter-filters__toggle")) {
                t.setAttribute("aria-expanded", "false");
                t.classList.remove("productgrid--sidebar-button-active");
              }
            });

          if (isOpen) {
            panel.classList.remove("filter-group-active");
            panel.dataset.accordionState = "closed";
            btn.setAttribute("aria-expanded", "false");
            btn.classList.remove("productgrid--sidebar-button-active");
          } else {
            panel.classList.add("filter-group-active");
            panel.dataset.accordionState = "open";
            btn.setAttribute("aria-expanded", "true");
            btn.classList.add("productgrid--sidebar-button-active");
          }
          return;
        }

        const clickedGroup  = btn.closest(".lb-filter-filters__group");
        const isAlreadyOpen = clickedGroup.classList.contains("is-open");

        container
          .querySelectorAll("ul.collection-filters__filter-colours[data-accordion-content]")
          .forEach(ul => {
            ul.classList.remove("filter-group-active");
            ul.dataset.accordionState = "closed";
            const t = ul.previousElementSibling;
            if (t?.matches?.("button.lb-filter-filters__toggle")) {
              t.setAttribute("aria-expanded", "false");
              t.classList.remove("productgrid--sidebar-button-active");
            }
          });

        container.querySelectorAll(".lb-filter-filters__group").forEach(g => {
          g.classList.remove("is-open");
          const t = g.querySelector(".lb-filter-filters__toggle");
          t?.setAttribute("aria-expanded", "false");
          t?.classList.remove("productgrid--sidebar-button-active");
        });
        if (!isAlreadyOpen) {
          clickedGroup.classList.add("is-open");
          btn.setAttribute("aria-expanded", "true");
          btn.classList.add("productgrid--sidebar-button-active");
        }
      });
    });
  });
}

// Chips above the grid from Luigi's `results.filters`. On collection PLP we hide the implicit `category:…` chip (not user-toggled).
function renderActiveFilterOptions(filters = []) {

    const isSearch = !!document.querySelector('[data-lb-search]');
    const applied = filters.filter(f => {
        if (f.startsWith("type:")) return false;
        if (!isSearch && f.startsWith("category:")) return false;
        return true;
    });

    const appliedDeduped = dedupeLbAppliedFilterStrings(applied);

    if (appliedDeduped.length === 0) {
        const existing = document.getElementById("lb-active-filters");
        if (existing) existing.remove();
        return;
    }

    const removeIconSvg = `<svg class="icon-remove" aria-hidden="true" focusable="false" role="presentation" xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10"><path fill="currentColor" d="M6.08785659,5 L9.77469752,1.31315906 L8.68684094,0.225302476 L5,3.91214341 L1.31315906,0.225302476 L0.225302476,1.31315906 L3.91214341,5 L0.225302476,8.68684094 L1.31315906,9.77469752 L5,6.08785659 L8.68684094,9.77469752 L9.77469752,8.68684094 L6.08785659,5 Z"></path></svg>`;

    let container = document.getElementById("lb-active-filters");
    if (!container) {
        container = document.createElement("div");
        container.id = "lb-active-filters";
        container.className = "lb-productgrid-filters";
        resultsContainer.insertAdjacentElement("beforebegin", container);
    }

    const chipLis = appliedDeduped.map((f) => {
        const colonIndex = f.indexOf(":");
        const field = f.slice(0, colonIndex);
        const value = f.slice(colonIndex + 1);
        const label = formatLbActiveFilterChipLabel(field, value);

        return `
          <li class="active-filter-group__item-wrapper">
            <button
              type="button"
              class="active-filter-group__item"
              data-field="${escapeHtmlAttr(field)}"
              data-value="${escapeHtmlAttr(value)}"
              aria-label="Remove filter ${escapeHtmlAttr(label)}"
            >
              <span class="active-filter-text">${escapeHtmlText(label)}</span>
              <span class="filter-icon--remove">${removeIconSvg}</span>
            </button>
          </li>`;
    });

    const clearLi =
        appliedDeduped.length >= 1
            ? `<li class="active-filter-group--remove-all filter-item--grid-simple">
            <button type="button" id="lb-clear-all" class="filter-item-clear-all items-center lb-filter-clear-all-btn">
              ${removeIconSvg}
              <span class="ff-heading">` +
              escapeHtmlText(window.theme.strings.lbClearAll) +
              `</span>
            </button>
          </li>`
            : "";

    container.innerHTML = `<ul class="active-filter-group">${chipLis.join("")}${clearLi}</ul>`;

    const clearAllBtn = container.querySelector("#lb-clear-all");
    if (clearAllBtn) {
      clearAllBtn.addEventListener("click", () => {
        activeFilters = {};
        if (document.querySelector('[data-lb-search]')) {
            getLBResults(searchResultsInput.value.trim(), activeFilters, 1, null);
        } else {
            getLBResults(null, activeFilters, 1, lbPlCategoryFilter());
        }
      });
    }

    container.querySelectorAll("button.active-filter-group__item[data-field]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const { field, value } = btn.dataset;

        if (activeFilters[field]) {
          if (field === LB_PRICE_AMOUNT_FACET) {
            activeFilters[field] = activeFilters[field].filter((v) => v !== value);
          } else {
            activeFilters[field] = lbRemoveActiveFacetValuesByMergeKey(activeFilters[field], value);
          }
          if (!activeFilters[field].length) delete activeFilters[field];
        }

        if (document.querySelector('[data-lb-search]')) {
            getLBResults(searchResultsInput.value.trim(), activeFilters, 1, null);
        } else {
            getLBResults(null, activeFilters, 1, lbPlCategoryFilter());
        }
      });
    });
}

// Page clicks re-fetch with `activeFilters` + `lbPlCategoryFilter()` on collections.
function renderPagination(totalHits, pageSizeForPagination) {
    if (!paginationContainer) return;
    const ps = Math.max(
        1,
        parseInt(String(pageSizeForPagination != null ? pageSizeForPagination : RESULTS_PER_PAGE), 10) || 24
    );
    const totalPages = Math.ceil((parseInt(String(totalHits || 0), 10) || 0) / ps);

    if (totalPages <= 1) { paginationContainer.innerHTML = ""; return; }

    const cur = currentPage;
    const pagesToShow = new Set([1, totalPages, cur - 1, cur, cur + 1]);

    // Near edges, mirror reference UX by widening the visible block instead of early ellipses.
    if (cur <= 3) {
        pagesToShow.add(2);
        pagesToShow.add(3);
    }
    if (cur >= totalPages - 2) {
        pagesToShow.add(totalPages - 1);
        pagesToShow.add(totalPages - 2);
    }

    const orderedPages = [...pagesToShow]
        .filter((p) => p >= 1 && p <= totalPages)
        .sort((a, b) => a - b);

    let html  = `<button ${cur === 1 ? "hidden" : ""} data-page="${cur - 1}" aria-label="` + window.theme.strings.lbPreviousLabel + `">
        <span class="pagination--chevron-left" aria-hidden="true">
              <svg aria-hidden="true" focusable="false" role="presentation" width="8" height="6" viewBox="0 0 8 6" fill="none" xmlns="http://www.w3.org/2000/svg" class="icon-chevron-down">
                <path class="icon-chevron-down-left" d="M4 4.5L7 1.5" stroke="currentColor" stroke-width="1.25" stroke-linecap="square"></path>
                <path class="icon-chevron-down-right" d="M4 4.5L1 1.5" stroke="currentColor" stroke-width="1.25" stroke-linecap="square"></path>
            </svg>
        </span>
        <span>` + window.theme.strings.lbPreviousLabel + `</span>
    </button>`;

    let previousPage = null;
    for (const p of orderedPages) {
        if (previousPage != null && p - previousPage > 1) {
            html += `<span class="lb-pagination-ellipsis" aria-hidden="true">…</span>`;
        }
        html += `<button class="${p === cur ? "active" : ""}" data-page="${p}" aria-label="Page ${p}" ${p === cur ? 'aria-current="page"' : ""}>${p}</button>`;
        previousPage = p;
    }

    html += `<button ${cur === totalPages ? "hidden" : ""} data-page="${cur + 1}" aria-label="` + window.theme.strings.lbNextLabel + `">
        <span>` + window.theme.strings.lbNextLabel + `</span>
        <span class="pagination--chevron-right" aria-hidden="true">
            <svg aria-hidden="true" focusable="false" role="presentation" width="8" height="6" viewBox="0 0 8 6" fill="none" xmlns="http://www.w3.org/2000/svg" class="icon-chevron-down">
                <path class="icon-chevron-down-left" d="M4 4.5L7 1.5" stroke="currentColor" stroke-width="1.25" stroke-linecap="square"></path>
                <path class="icon-chevron-down-right" d="M4 4.5L1 1.5" stroke="currentColor" stroke-width="1.25" stroke-linecap="square"></path>
            </svg>
        </span>
    </button>`;

    paginationContainer.innerHTML = html;

    paginationContainer.querySelectorAll("button[data-page]").forEach(btn => {
        if (document.querySelector("[data-lb-search]")) {
            btn.addEventListener("click", () =>
                getLBResults(searchResultsInput.value.trim(), activeFilters, +btn.dataset.page, null, {
                    scrollToGrid: true,
                })
            );
        } else {
            btn.addEventListener("click", () =>
                getLBResults(null, activeFilters, +btn.dataset.page, lbPlCategoryFilter(), {
                    scrollToGrid: true,
                })
            );
        }
    });
}

function refreshLbResultsPage1() {
    if (document.querySelector("[data-lb-search]")) {
        getLBResults(searchResultsInput.value.trim(), activeFilters, 1, null);
    } else {
        getLBResults(null, activeFilters, 1, lbPlCategoryFilter());
    }
}

function wireLbPriceFacetControls(container) {
    if (!container || !container.querySelector("[data-lb-price-custom]")) return;

    const applyFromGroup = (group) => {
        const minEl = group.querySelector(".lb-price-custom-min");
        const maxEl = group.querySelector(".lb-price-custom-max");
        if (!minEl || !maxEl) return;
        const { nMin, nMax } = parseLbCustomPriceRangeInputs(minEl.value, maxEl.value);
        activeFilters[LB_PRICE_AMOUNT_FACET] = [`${nMin} | ${nMax}`];
        refreshLbResultsPage1();
    };

    container.querySelectorAll("[data-lb-price-custom]").forEach((wrap) => {
        const group = wrap.closest(".lb-filter-filters__options");
        if (!group) return;
        wrap.querySelector(".lb-price-custom-apply")?.addEventListener("click", () => applyFromGroup(group));
        wrap.querySelectorAll(".lb-price-custom-min, .lb-price-custom-max").forEach((inp) => {
            inp.addEventListener("keydown", (e) => {
                if (e.key === "Enter") {
                    e.preventDefault();
                    applyFromGroup(group);
                }
            });
        });
    });
}

// Checkbox changes mutate `activeFilters` then reset to page 1 (search vs PLP branch same as elsewhere).
function handleFacetChange(e) {
    const { facet, value } = e.target.dataset;

    if (!activeFilters[facet]) activeFilters[facet] = [];

    if (e.target.checked) {
        if (facet === LB_PRICE_AMOUNT_FACET) {
            activeFilters[facet] = activeFilters[facet].filter((v) => !isLbCustomPriceRangeFilterValue(v));
        }
        if (facet === LB_PRICE_AMOUNT_FACET) {
            if (!activeFilters[facet].includes(value)) {
                activeFilters[facet].push(value);
            }
        } else {
            activeFilters[facet] = lbRemoveActiveFacetValuesByMergeKey(activeFilters[facet], value);
            activeFilters[facet].push(value);
        }
    } else {
        if (facet === LB_PRICE_AMOUNT_FACET) {
            activeFilters[facet] = activeFilters[facet].filter((v) => v !== value);
        } else {
            activeFilters[facet] = lbRemoveActiveFacetValuesByMergeKey(activeFilters[facet], value);
        }
        if (!activeFilters[facet].length) delete activeFilters[facet];
    }

    refreshLbResultsPage1();
}

// Shallow URL state for share/back: `q`, `page`, `f[facet]=value` (mirrors theme filter param style where applicable).
function updateURL(query, filters, page) {
    const params = new URLSearchParams();

    if (query) params.set("q", query);
    if (page > 1) params.set("page", page);

    for (const [key, vals] of Object.entries(filters)) {
        vals.forEach(v => params.append(`f[${key}]`, v));
    }
    const defaultSort = (sortSelect && sortSelect.getAttribute("data-lb-default-sort")) || "";
    if (sortSelect && sortSelect.value && sortSelect.value !== defaultSort) {
        params.set("sort_by", sortSelect.value);
    }
    const qs = params.toString();
    const hash = location.hash || "";
    history.replaceState(null, "", qs ? `${location.pathname}?${qs}${hash}` : `${location.pathname}${hash}`);
}

// Initial load / refresh: rebuild `activeFilters` object from `f[...]` keys.
function readURL() {
    const params = new URLSearchParams(location.search);
    const q      = params.get("q");
    const page   = parseInt(params.get("page") || "1", 10);

    const restored = {};
    params.forEach((value, key) => {
        const match = key.match(/^f\[(.+)\]$/);
        if (match) {
            const facet = match[1];
            if (!restored[facet]) restored[facet] = [];
            restored[facet].push(value);
        }
    });

    return { q, page, filters: restored, sortBy: params.get("sort_by") };
}

// GA4 / GTM dataLayer — same `filters` shape for PLP + Search (`scopes` only on PLP).
function lbFiltersForAnalytics(filters = {}) {
    const out = {};
    for (const key in filters) {
        out[key] = filters[key].join(',');
    }
    return out;
}

/** Canonical `shopify_CC_<product>_<variant>` — feed first, then Shopify `.js` cache after card hydrate. */
function lbItemIdForLuigiHit(hit) {
    if (
        window.theme &&
        typeof window.theme.lbGaItemIdFromLuigiHit === "function" &&
        typeof window.theme.lbIsCanonicalGaItemId === "function"
    ) {
        const fromFeed = window.theme.lbGaItemIdFromLuigiHit(hit);
        if (window.theme.lbIsCanonicalGaItemId(fromFeed)) return fromFeed;
    }
    const path = lbHitProductPathForFetch(hit);
    if (!path || !__lbShopifyProductByPath.has(path)) return "";
    const ent = __lbShopifyProductByPath.get(path);
    if (!ent?.product?.id) return "";
    const variant = lbShopifySelectedOrFirstVariant(ent.product);
    const id =
        window.theme &&
        typeof window.theme.gaCanonicalItemId === "function" &&
        window.theme.gaCanonicalItemId(ent.product.id, variant?.id);
    return id && window.theme.lbIsCanonicalGaItemId(id) ? id : "";
}

function lbEcommerceItemFromLuigiHit(hit, index) {
    const itemId = lbItemIdForLuigiHit(hit);
    if (!itemId) return null;
    return {
        item_id: itemId,
        item_name: hit.attributes.title,
        index: (currentPage - 1) * lastLuigiRequestPageSize + index + 1,
        price: hit.attributes.price_amount,
        type: hit.type === "category" ? "category" : "product",
    };
}

function trackSearchView(query, hits, activeFiltersState = {}) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "view_item_list",
      ecommerce: {
        item_list_name: "Search Results",
        search_term: query,
        items: hits
            .map((hit, index) => lbEcommerceItemFromLuigiHit(hit, index))
            .filter(Boolean),
        filters: lbFiltersForAnalytics(activeFiltersState),
      },
    });
}

function trackListView(hits, categoryLabel, categoryIdentityForScope, subsequentFilters = {}) {
    if (!hits || hits.length === 0) return;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "view_item_list",
      ecommerce: {
        item_list_name: "Product Listing",
        items: hits
            .map((hit, index) => lbEcommerceItemFromLuigiHit(hit, index))
            .filter(Boolean),
        scopes: {
          CategoryLabel: categoryLabel,
          CategoryIdentity:
              categoryIdentityForScope != null && String(categoryIdentityForScope).length > 0
                  ? String(categoryIdentityForScope)
                  : String(categoryLabel || ""),
        },
        filters: lbFiltersForAnalytics(subsequentFilters)
      }
    });
}

function trackClickEvent(itemId) {
    if (
        !itemId ||
        !window.theme ||
        typeof window.theme.lbIsCanonicalGaItemId !== "function" ||
        !window.theme.lbIsCanonicalGaItemId(itemId)
    ) {
        return;
    }
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "select_item",
      ecommerce: {
        items: [
          {
            item_id: itemId
          }
        ]
      }
    });
}

if (document.querySelector("[data-lb-search]") && searchResultsForm) {
    searchResultsForm.addEventListener("submit", e => {
        e.preventDefault();
        activeFilters = {};
        currentPage   = 1;
        getLBResults(searchResultsInput.value.trim(), activeFilters, 1, null);
    });
}

if (sortSelect) {
    sortSelect.addEventListener("change", () => {
        if (lbIgnoreSortChangeDepth > 0) return;
        if (document.querySelector('[data-lb-search]')) {
            getLBResults(searchResultsInput.value.trim(), activeFilters, 1, null);
        } else {
            getLBResults(null, activeFilters, 1, lbPlCategoryFilter());
        }
    });
}

// Boot: read `?q`, `?page`, `?f[facet]=`, `?sort_by=` from the URL, then run the first Luigi request.
const { q, page, filters, sortBy } = readURL();
activeFilters = filters;

if (sortBy && sortSelect) {
    const match = Array.from(sortSelect.options).find((o) => o.value === sortBy);
    if (match) {
        lbWithIgnoredSortSelectChange(() => {
            sortSelect.value = sortBy;
        });
    }
}

if (document.querySelector("[data-lb-search]")) {
    if (searchResultsInput) searchResultsInput.value = q;
    getLBResults(q, activeFilters, page, null);
} else if (categoryTitle) {
    getLBResults(null, activeFilters, page, lbPlCategoryFilter());
} else if (document.querySelector("[data-lb-collection]")) {
    console.warn(
        "Luigi collection: data-lb-plp-category is empty — check lb-collection section output."
    );
}